// src/FormBuilder.jsx
// src/FormBuilder.jsx
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { nanoid } from "nanoid";
import { collection, addDoc, doc, setDoc, getDoc, getDocs, query, orderBy } from "firebase/firestore"; // ✅ added getDoc, getDocs, query, orderBy
import { db, storage } from "./firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { motion, AnimatePresence } from "framer-motion";
import logo from "./assets/image/logo_web.png";
import { serverTimestamp } from "firebase/firestore";
import { getCurrentUserCompanyId, getCurrentUserData } from "./utils/userUtils";
import { hasPermission } from './utils/permissions';
import {
  buildNewDocumentControl,
  buildUpdatedDocumentControl,
  getCurrentRevision,
} from "./utils/documentControl";
import { SPLIT_WORKFLOW_TYPE, isWorkflowSection } from "./utils/workflowUtils";
import FieldRenderer from "./components/FieldRenderer";
import AccessAssignment from "./components/AccessAssignment";
import ScheduleManager from "./components/ScheduleManager";
import ApprovalWorkflowManager from "./components/ApprovalWorkflowManager";
import { useTranslation } from 'react-i18next';
const LAYOUT_KEY = "bmc:builderLayout:v3";
const FORM_DRAFT_KEY = "bmc:builderDraft:v1";


// -------------------- Field Catalog --------------------
const FIELD_TYPES = [
  { type: "instruction", labelKey: "formBuilder.fieldInstruction" },
  { type: "document_number", labelKey: "formBuilder.fieldDocumentNumber" },
  { type: "date_time", labelKey: "formBuilder.fieldDateTime" },
  { type: "location", labelKey: "formBuilder.fieldLocation" },
  { type: "asset", labelKey: "formBuilder.fieldAsset" },
  { type: "text", labelKey: "formBuilder.fieldText" },
  { type: "number", labelKey: "formBuilder.fieldNumber" },
  { type: "checkbox", labelKey: "formBuilder.fieldCheckbox" },
  { type: "multiple_choice", labelKey: "formBuilder.fieldMultipleChoice" },
  { type: "slider", labelKey: "formBuilder.fieldSlider" },
  { type: "media", labelKey: "formBuilder.fieldMedia" },
  { type: "signature", labelKey: "formBuilder.fieldSignature" },
  { type: "yesno", labelKey: "formBuilder.fieldYesNo" },
  { type: "section", labelKey: "formBuilder.fieldSection" },
  { type: SPLIT_WORKFLOW_TYPE, labelKey: "formBuilder.fieldSplitWorkflow" },
];




const highlightClass =
  "ring-2 ring-rose-400 ring-offset-2 dark:ring-offset-gray-900 transition-all";


function labelFor(type, t) {
  const key = FIELD_TYPES.find((ft) => ft.type === type)?.labelKey;
  return key && t ? t(key) : FIELD_TYPES.find((ft) => ft.type === type)?.labelKey?.split('.').pop() || "Question";
}

// Add this near top (after imports) 
function toggleDarkMode() { document.documentElement.classList.toggle("dark");
}




// -------------------- Defaults --------------------
function defaultFieldConfig(type) {
  switch (type) {
    case "instruction":
      return { text: "Enter your instruction here...", attachments: [], searchable: false };
    case "document_number":
      return { prefix: "TEST", pad: 6, nextNumber: 1, searchable: false };
    case "date_time":
      return { mode: "datetime", searchable: false }; // date | time | datetime
    case "location":
      return { mode: "manual", searchable: false }; // manual | gps | preset
    case "asset":
      return { from: "assets", searchable: false };
    case "number":
      return { min: null, max: null, step: 1, searchable: false, unit: "none" };
    case "slider":
      return { min: 1, max: 5, step: 1, showValue: true, searchable: false };
    case "multiple_choice":
      return {
        options: [
          { id: nanoid(), label: "Checked", score: 1, color: "#fee2e2" },
          { id: nanoid(), label: "Unchecked", score: 0, color: "#e5e7eb" },
          { id: nanoid(), label: "N/A", score: 0, color: "#fef3c7" },
        ],
        allowOther: false,
        multi: false,
        searchable: false,
      };
    case "media":
      return { allowMultiple: true, files: [], searchable: false };
    case "signature":
      return { requireTypedName: true, searchable: false };
    case "yesno":
      return { yesColor: "#22c55e", noColor: "#ef4444", searchable: false };
    case "section":
      return { repeatable: false }; // ✅ sections don't need searchable flag
    case SPLIT_WORKFLOW_TYPE:
      return {
        repeatable: false,
        assignedUsers: [],
        assignedGroups: [],
        allUsers: true,
        allGroups: false,
        previousSectionVisibility: "visible",
      };
    default:
      return { searchable: false };
  }
}

function makeField(type, t) {
  return {
    id: nanoid(),
    type,
    label: labelFor(type, t),
    required: false,
    config: defaultFieldConfig(type),
    branchMap: {},
    attachments: [], // per-field static files to show during checks
  };
}


// -------------------- Storage Helper --------------------
async function uploadToStorage(companyId, fieldId, file) {
  // dynamically import Firebase storage to avoid race conditions
  const { storage } = await import("./firebase");
  const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");

  const path = `companies/${companyId}/builder-temp/${fieldId}/${Date.now()}_${file.name}`;
  const fileRef = ref(storage, path);

  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);

  return {
    name: file.name,
    type: file.type,
    size: file.size,
    url,
  };
}

// -------------------- Small utils --------------------
const isImage = (mime = "") => mime.startsWith("image/");
const isPdf = (mime = "") => mime === "application/pdf";
const iconFor = (mime = "") => (isPdf(mime) ? "📄" : isImage(mime) ? "🖼️" : "📎");

function hexToRgb(hex) {
  const v = hex?.replace("#", "");
  if (!v || (v.length !== 3 && v.length !== 6)) return { r: 229, g: 231, b: 235 };
  const n = v.length === 3 ? v.split("").map((c) => c + c).join("") : v;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return { r, g, b };
}
function isDark(hex) {
  const { r, g, b } = hexToRgb(hex);
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L < 0.6;
}
function darkenHex(hex, factor = 0.9) {
  const { r, g, b } = hexToRgb(hex);
  const rd = Math.max(0, Math.min(255, Math.round(r * factor)));
  const gd = Math.max(0, Math.min(255, Math.round(g * factor)));
  const bd = Math.max(0, Math.min(255, Math.round(b * factor)));
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(rd)}${toHex(gd)}${toHex(bd)}`;
}
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

function getBranchGroups(field) {
  switch (field.type) {
    case "yesno":
      return [
        { key: "yes", label: "When answer is Yes", test: (v) => String(v?.answer ?? v).toLowerCase() === "yes" },
        { key: "no", label: "When answer is No", test: (v) => String(v?.answer ?? v).toLowerCase() === "no" },
      ];
    case "checkbox":
      return [
        { key: "checked", label: "When checkbox is checked", test: (v) => (v === true) || (v?.checked === true) },
        { key: "unchecked", label: "When checkbox is not checked", test: (v) => (v === false) || (v?.checked === false) },
      ];
    case "text":
      return [
        { key: "not_blank", label: "When answer is not blank", test: (v) => !!(v?.answer ?? v) && String((v?.answer ?? v)).trim().length > 0 },
      ];
    case "number":
      return [
        {
          key: "not_blank",
          label: "When number is provided",
          test: (v) => {
            const actual = v?.number ?? v?.answer ?? v;
            return actual !== null && actual !== "" && !isNaN(actual);
          },
        },
      ];
    case "date_time":
      return [
        {
          key: "not_blank",
          label: "When date/time is provided",
          test: (v) => !!v,
        },
      ];
    case "signature":
      return [
        {
          key: "signed",
          label: "When signed / typed",
          test: (v) => !!v && (typeof v === "string" ? v.trim().length > 0 : true),
        },
      ];
    case "multiple_choice":
      return (field.config?.options || []).map((opt) => ({
        key: `opt_${opt.id || opt.label}`,
        label: `When selected: ${opt.label}`,
        test: (v) => (Array.isArray(v) ? v.includes(opt.label) : String(v) === String(opt.label)),
      }));
    case "asset":
    case "location":
      return [
        {
          key: "not_blank",
          label: "When a value is selected",
          test: (v) => !!v && String(v).trim().length > 0,
        },
      ];
    default:
      return [];
  }
}

function hasAnyBranch(field) {
  return !!field.branchMap && Object.keys(field.branchMap).length > 0;
}

// Visual branch index for depth/indent
function buildBranchIndex(fields) {
  const parentsOf = new Map(); // childId -> Set(parentId)
  const visit = (list) => {
    list.forEach((src) => {
      const groups = getBranchGroups(src);
      groups.forEach((g) => {
        const cfg = src.branchMap?.[g.key];
        if (!cfg?.targets?.length) return;
        cfg.targets.forEach((tid) => {
          if (!parentsOf.has(tid)) parentsOf.set(tid, new Set());
          parentsOf.get(tid).add(src.id);
        });
      });

      if (src.children?.length) visit(src.children);
      if (src.instances?.length) {
        src.instances.forEach((inst) => inst.children?.length && visit(inst.children));
      }
    });
  };

  visit(fields);

  const depthMemo = new Map();
  function depthOf(id, stack = new Set()) {
    if (depthMemo.has(id)) return depthMemo.get(id);
    if (stack.has(id)) return 0;
    stack.add(id);
    const parents = Array.from(parentsOf.get(id) || []);
    const d = parents.length === 0 ? 0 : 1 + Math.max(...parents.map((p) => depthOf(p, stack)));
    depthMemo.set(id, d);
    return d;
  }

  return { isTarget: (id) => parentsOf.has(id), depth: (id) => depthOf(id) };
}

// -------------------- Visibility --------------------
// ---------- Visibility helper ----------
function computeVisibility(fields, previewValues) {
  // Build a flat lookup table for all fields
  const byId = new Map();
  const flatten = (list) => {
    for (const f of list) {
      byId.set(f.id, f);
      if (f.children?.length) flatten(f.children);
    }
  };
  flatten(fields);

  // Track all branch targets
  const targetedBy = new Map();
  for (const [id, f] of byId) {
    const groups = getBranchGroups(f);
    for (const g of groups) {
      const cfg = f.branchMap?.[g.key];
      if (!cfg?.targets?.length) continue;
      for (const targetId of cfg.targets) {
        if (!byId.has(targetId)) continue; // skip missing
        if (!targetedBy.has(targetId)) targetedBy.set(targetId, []);
        targetedBy.get(targetId).push({ sourceId: id, key: g.key });
      }
    }
  }

  // Start with all visible
  const vis = new Map([...byId.keys()].map((id) => [id, true]));

  // Hide all direct branch targets initially
  for (const tid of targetedBy.keys()) vis.set(tid, false);

  // Evaluate recursively only once per field
  const evaluate = (field) => {
    const value = previewValues[field.id];
    const groups = getBranchGroups(field);
    for (const g of groups) {
      const cfg = field.branchMap?.[g.key];
      if (!cfg?.targets?.length) continue;
      const passes = g.test(value, field);
      for (const t of cfg.targets) {
        if (passes) vis.set(t, true);
      }
    }

    // Recurse children
    if (field.children?.length) {
      field.children.forEach(evaluate);
    }
  };

  fields.forEach(evaluate);

  // Ensure visible children keep parents visible
  for (const [id, f] of byId) {
    if (f.children?.some((c) => vis.get(c.id))) {
      vis.set(id, true);
    }
  }

  return (id) => vis.get(id) !== false;
}






// -------------------- Sortable Canvas Item --------------------
function SortableItem({
  id,
  field,
  selected,
  onSelect,
  visualDepth,
  isBranched,
  onDuplicate,
  updateField,
  updateFieldConfig, // ✅ add this
}) {
  const { t: translate } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const borderColor = selected ? "border-rose-300/70" : isBranched ? "border-rose-200/60" : "border-white/20";
  const bgColor = isBranched ? "bg-rose-900/50 backdrop-blur" : "bg-slate-900/60 backdrop-blur";
  const leftPad = Math.min(visualDepth, 6) * 16;

  return (
    <motion.div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onSelect(field.id)}
      style={{ ...style, marginLeft: leftPad }}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      whileHover={{ y: -1, boxShadow: "0 10px 24px rgba(0,0,0,0.06)" }}
      whileTap={{ scale: 0.995 }}
      className={`${bgColor} border ${borderColor} rounded-xl p-3 mb-2 cursor-grab transition-colors relative ${isDragging ? "shadow-lg shadow-black/30" : "shadow-sm"} ${selected ? "ring-2 ring-rose-300/80" : ""} text-white`}
    >
      {isBranched && <div className="absolute -left-3 top-2 bottom-2 w-[3px] bg-rose-400 rounded-full" />}

      <div className="flex items-center justify-between">
  <strong className="text-white flex items-center gap-2">
    {field.label}
    {field.config?.searchable && (
      <span className="text-[10px] text-emerald-200 italic">
        🔍 {translate('formBuilder.searchableTag')}
      </span>
    )}
  </strong>
  <div className="flex items-center gap-2">
    <span className="text-xs text-white/70">{field.type}</span>
    <button
      onClick={(e)=>{e.stopPropagation(); onDuplicate(field.id);}}
      className="text-xs px-2 py-1 rounded-md border border-white/20 bg-white/10 hover:bg-white/20 text-white"
      title={translate('formBuilder.duplicateField')}
    >
      ⧉
    </button>
  </div>
</div>
      {field.required && <div className="text-xs text-rose-200 mt-1">{translate('formBuilder.required')}</div>}
      {hasAnyBranch(field) && <div className="text-[11px] text-white/70 mt-1 italic">{translate('formBuilder.branchingConfigured')}</div>}

      {/* inline attachment chips (icons/mini thumbnails) */}
      {field.attachments?.length > 0 && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {field.attachments.map((att) => (
            <AttachmentChip key={att.url} file={att} />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// -------------------- Toolbox Draggable --------------------
function DraggableToolboxItem({ type, label }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: `tool:${type}`, data: { from: "toolbox", fieldType: type } });

  const style = { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.5 : 1 };

  return (
    <motion.button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      whileHover={{ y: -1, boxShadow: "0 10px 24px rgba(244,63,94,0.10)" }}
      whileTap={{ scale: 0.98 }}
      className="flex items-center justify-between text-left bg-white/10 border border-white/15 rounded-lg px-3 py-2 hover:bg-white/15 transition cursor-grab w-full text-sm font-medium text-white shadow-sm"
      title="Drag to canvas"
    >
      <span>{label}</span>
      <span className="text-xs text-white/70">⇢</span>
    </motion.button>
  );
}

// -------------------- Droppable helpers --------------------
function CanvasDropArea({ children, id = "canvas-drop" }) {
  const { t: translate } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <motion.div
      ref={setNodeRef}
      animate={{
        borderColor: isOver ? "rgb(244,63,94)" : "rgb(209,213,219)",
        backgroundColor: isOver
          ? "rgba(254,205,211,0.45)"
          : "transparent",
        scale: isOver ? 1.01 : 1,
      }}
      transition={{ duration: 0.18 }}
      className="mb-3 border-2 border-dashed rounded-xl p-3 text-center relative overflow-hidden"
    >
      {children || translate('formBuilder.dropFieldHere')}
      {isOver && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-rose-50/50 to-rose-100/20 rounded-xl pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}
    </motion.div>
  );
}


function CanvasInsertBar({ id }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <motion.div
      ref={setNodeRef}
      animate={{ backgroundColor: isOver ? "rgba(244,63,94,0.28)" : "rgba(0,0,0,0)" }}
      transition={{ duration: 0.16 }}
      className="h-4 my-1 rounded"
      title="Drop here to insert"
    />
  );
}

function BranchDropArea({ id, children }) {
  const { t: translate } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <motion.div
      ref={setNodeRef}
      animate={{
        borderColor: isOver ? "rgb(239,68,68)" : "rgb(209,213,219)", // red-500
        backgroundColor: isOver
          ? "rgba(254,205,211,0.35)"
          : "transparent",
        scale: isOver ? 1.02 : 1,
        boxShadow: isOver
          ? "0 0 10px rgba(239,68,68,0.25)"
          : "none",
      }}
      transition={{ duration: 0.18 }}
      className="border-2 border-dashed rounded-md p-2 min-h-[48px] relative"
    >
      {children || (
        <span className="text-xs text-white/70">{translate('formBuilder.dropBranchFieldsHere')}</span>
      )}
      {isOver && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-rose-100/40 to-rose-200/20 rounded-md pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}
    </motion.div>
  );
}


// -------------------- Section Drop Area (blue style) --------------------
function SectionDropArea({ id, children }) {
  const { t: translate } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <motion.div
      ref={setNodeRef}
      animate={{
        borderColor: isOver
          ? "rgb(37,99,235)" // blue-600
          : "rgb(191,219,254)", // blue-300
        backgroundColor: isOver
          ? "rgba(59,130,246,0.15)"
          : "rgba(59,130,246,0.05)",
        scale: isOver ? 1.02 : 1,
        boxShadow: isOver
          ? "0 0 12px rgba(59,130,246,0.35)"
          : "0 0 0 rgba(0,0,0,0)",
      }}
      transition={{ duration: 0.18 }}
      className="border-2 border-dashed rounded-xl p-3 relative overflow-hidden"
    >
      {children || (
        <p className="text-xs text-blue-700 dark:text-blue-100 text-center italic py-2">
          {translate('formBuilder.dropFieldsHere')}
        </p>
      )}

      {/* Gradient overlay when hovered */}
      {isOver && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-blue-100/40 to-blue-200/20 rounded-xl pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}
    </motion.div>
  );
}


// -------------------- Section Canvas --------------------
function SectionCanvas({
  f,
  selectedId,
  setSelectedId,
  branchIndex,
  duplicateField,
  updateField,
  updateFieldConfig,
}) {
  const { t: translate } = useTranslation();


  const repeatable = !!f.config?.repeatable;
  const instances = f.instances || [f]; // default: one instance if none yet

  function addInstance() {
    const copy = JSON.parse(JSON.stringify(f));
    copy.id = nanoid();
    copy.children = (f.children || []).map((c) => ({
      ...JSON.parse(JSON.stringify(c)),
      id: nanoid(),
    }));

    updateField(f.id, {
      instances: [...instances, copy],
    });
  }

  return (
    <motion.div
      key={f.id}
      className="relative mb-4 border-2 border-blue-400 dark:border-blue-700 rounded-2xl p-3 bg-blue-50/40 dark:bg-blue-900/20 transition-all"
    >
      {/* Section Header */}
      <div className="flex items-center justify-between mb-2">
        <strong className="text-blue-700 dark:text-blue-300 flex items-center gap-2">
          📁 {f.label || translate('formBuilder.untitledSection')}
        </strong>

        <div className="flex items-center gap-2">
          {repeatable && (
            <button
              onClick={addInstance}
              className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded"
              title={translate('formBuilder.addAnotherTitle')}
            >
              ➕ {translate('formBuilder.addAnother')}
            </button>
          )}

          {/* Repeatable toggle */}
          <label className="text-xs flex items-center gap-1 cursor-pointer text-blue-600 dark:text-blue-400">
            <input
              type="checkbox"
              checked={repeatable}
              onChange={(e) =>
                updateFieldConfig(f.id, { repeatable: e.target.checked })
              }
            />
            {translate('formBuilder.repeatable')}
          </label>
        </div>
      </div>

      {/* Render each section instance */}
      {instances.map((inst, idx) => (
        <div
          key={inst.id}
          className="mb-3 border border-blue-300 dark:border-blue-800 rounded-lg p-2 bg-white/50 dark:bg-blue-950/20"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-blue-500">{translate('formBuilder.instance')} #{idx + 1}</span>
            {repeatable && instances.length > 1 && (
              <button
                className="text-xs text-rose-500 hover:text-rose-700"
                onClick={() =>
                  updateField(f.id, {
                    instances: instances.filter((x) => x.id !== inst.id),
                  })
                }
              >
                ✕ {translate('formBuilder.removeInstance')}
              </button>
            )}
          </div>

          <SectionDropArea id={`section-drop:${inst.id}`}>
            {inst.children && inst.children.length > 0 ? (
              inst.children.map((child) => (
                <SortableItem
                  key={child.id}
                  id={child.id}
                  field={child}
                  selected={selectedId === child.id}
                  onSelect={setSelectedId}
                  visualDepth={branchIndex.depth(child.id)}
                  isBranched={branchIndex.isTarget(child.id)}
                  onDuplicate={duplicateField}
                  updateField={updateField}
                  updateFieldConfig={updateFieldConfig}
                />
              ))
            ) : (
              <p className="text-xs text-gray-700 dark:text-gray-200 italic text-center py-2">
                {translate('formBuilder.dropFieldsHere')}
              </p>
            )}
          </SectionDropArea>
        </div>
      ))}
    </motion.div>
  );
}



// -------------------- Attachment chip --------------------
function AttachmentChip({ file }) {
  const { type, url, name } = file || {};
  const isImg = isImage(type);
  return (
    <div className="flex items-center gap-2 border border-white/20 rounded-md px-2 py-1 bg-slate-900/70 text-white shadow-sm">
      {isImg ? (
        <img src={url} alt={name} className="w-6 h-6 object-cover rounded-sm" />
      ) : (
        <span className="text-sm">{iconFor(type)}</span>
      )}
      <span className="text-xs text-white/80 max-w-[140px] truncate" title={name}>
        {name}
      </span>
    </div>
  );
}

// -------------------- Resize Handle --------------------
function ResizeHandle({ onMouseDown, title = "Drag to resize" }) {
  return (
    <div
      onMouseDown={onMouseDown}
      title={title}
      className="relative w-[10px] cursor-col-resize select-none group"
    >
      <div className="absolute inset-y-0 left-0 right-0 bg-rose-100 group-hover:bg-rose-200 transition-all ease-in-out" />
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-rose-400 group-hover:bg-rose-500 transition-all ease-in-out" />
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex flex-col items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-1 h-1 rounded bg-rose-500" />
        <div className="w-1 h-1 rounded bg-rose-500" />
        <div className="w-1 h-1 rounded bg-rose-500" />
      </div>
      <div className="absolute inset-0 rounded-sm group-hover:shadow-[0_0_0_2px_rgba(244,63,94,0.25)] pointer-events-none" />
    </div>
  );
}

// -------------------- Main --------------------
export default function FormBuilder({ formId = null, onBack, initialFormData = null, selectedFolderId: initialSelectedFolderId = null }) {
  // ✅ 1. Helper defined first (before useMemo)
  function collectAllBranchTargets(list) {
    const targets = new Set();

    function walk(items) {
      for (const f of items) {
        // branch targets on this field
        for (const cfg of Object.values(f.branchMap || {})) {
          if (cfg?.targets?.length) {
            cfg.targets.forEach((tid) => targets.add(tid));
          }
        }

        // normal children
        if (f.children?.length) walk(f.children);

        // repeatable section instances
        if (f.instances?.length) {
          for (const inst of f.instances) {
            if (inst.children?.length) walk(inst.children);
          }
        }
      }
    }

    walk(list);
    return targets;
  }

  // ✅ 2. State and hooks
  const { t: translate } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [title, setTitle] = useState("");
  const [fields, setFields] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [toolSearch, setToolSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState("mobile");
  const [showPropsInTablet, setShowPropsInTablet] = useState(true);
  const [cleanPreviewMode, setCleanPreviewMode] = useState(true);
  const [previewScrollTop, setPreviewScrollTop] = useState(0);
  const [previewValues, setPreviewValues] = useState({});
  const [previewHostSize, setPreviewHostSize] = useState({ width: 0, height: 0 });
  const [activeToolLabel, setActiveToolLabel] = useState("");
  const [uploadingField, setUploadingField] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(initialSelectedFolderId || null);
  // Access control state
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [allUsers, setAllUsers] = useState(true); // Default to all users
  const [allGroups, setAllGroups] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetFolders, setAssetFolders] = useState([]);
  const [revisionSummary, setRevisionSummary] = useState("");
  const previewScrollRef = useRef(null);
  const previewHostRef = useRef(null);
  const [revisionComments, setRevisionComments] = useState("");
  const [panelSizes, setPanelSizes] = useState({
    toolbox: 280,
    canvas: 660,
    props: 380,
    previewMin: 420,
  });
  const lastEditedDate = (() => {
    if (!updatedAt) return null;
    if (typeof updatedAt?.toDate === "function") return updatedAt.toDate();
    const parsed = new Date(updatedAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  })();
  const [collapsed, setCollapsed] = useState({
    toolbox: false,
    canvas: false,
    props: false,
    preview: false,
  });
  const [activeBuilderTab, setActiveBuilderTab] = useState("fields"); // "fields" | "schedules" | "approvals"

  // Ensure the page background matches other pages in light mode (bg-gray-200)
  useEffect(() => {
    try {
      document.body.classList.add('min-h-screen');
      const isDark = document.documentElement.classList.contains('dark');
      if (isDark) {
        document.body.classList.add('bg-gray-900');
      } else {
        document.body.classList.add('bg-gray-200');
      }
      return () => {
        document.body.classList.remove('min-h-screen');
        document.body.classList.remove('bg-gray-200');
        document.body.classList.remove('bg-gray-900');
      };
    } catch (e) {
      // ignore in non-browser environments
    }
  }, []);

  // ✅ 3. Hook safely using helper (no ESLint errors)
  const allBranchTargets = useMemo(() => collectAllBranchTargets(fields), [fields]);

  // ✅ 4. Layout constraints
  const minW = { toolbox: 180, canvas: 420, props: 300, previewMin: 320 };
  const maxW = { toolbox: 540, canvas: 1400, props: 760 };

  // ---------- Load existing form if editing 🔥 ----------
  useEffect(() => {
    async function loadForm() {
      if (!formId) return;
      try {
        const userCompanyId = await getCurrentUserCompanyId();
        if (!userCompanyId) {
          console.error("No company assigned to user");
          return;
        }
        setCompanyId(userCompanyId);

        const ref = doc(db, `companies/${userCompanyId}/forms/${formId}`);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setTitle(data.title || "");
          setFields(Array.isArray(data.fields) ? flattenSplitWorkflowFieldsForBuilder(data.fields) : []);
          setUpdatedAt(data.updatedAt || null);
          setSelectedFolderId(data.folderId || null);
          // Load access control data
          setAssignedUsers(data.assignedUsers || []);
          setAssignedGroups(data.assignedGroups || []);
          setAllUsers(data.allUsers !== undefined ? data.allUsers : true);
          setAllGroups(data.allGroups !== undefined ? data.allGroups : false);
          setOriginalForm({
            ...data,
            fields: Array.isArray(data.fields) ? flattenSplitWorkflowFieldsForBuilder(data.fields) : [],
          });
          setRevisionSummary("");
          setRevisionComments("");
        } else {
          console.warn("Form not found:", formId);
        }
      } catch (err) {
        console.error("Failed to load form:", err);
      }
    }
    loadForm();
  }, [formId]);

  // ---------- Load folders ----------
  useEffect(() => {
    async function loadFolders() {
      try {
        const userCompanyId = await getCurrentUserCompanyId();
        if (!userCompanyId) return;

        // Set companyId so assets can load
        setCompanyId(userCompanyId);

        const foldersRef = collection(db, `companies/${userCompanyId}/folders`);
        const foldersQuery = query(foldersRef, orderBy('name'));
        const foldersSnap = await getDocs(foldersQuery);
        
        const foldersList = foldersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setFolders(foldersList);
      } catch (err) {
        console.error("Failed to load folders:", err);
      }
    }
    loadFolders();
  }, []);

  // ---------- Restore layout + local drafts ----------
  useEffect(() => {
    if (formId) return;

    // If initialFormData provided (from document upload), use it
    if (initialFormData) {
      if (initialFormData.title) setTitle(initialFormData.title);
      if (Array.isArray(initialFormData.fields)) {
        setFields(flattenSplitWorkflowFieldsForBuilder(initialFormData.fields));
      }
      return;
    }

    const raw = localStorage.getItem(FORM_DRAFT_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.fields?.length > 0) {
          if (parsed?.title) setTitle(parsed.title);
          setFields(flattenSplitWorkflowFieldsForBuilder(parsed.fields));
        } else {
          localStorage.removeItem(FORM_DRAFT_KEY);
          setTitle("");
          setFields([]);
        }
      } catch {
        localStorage.removeItem(FORM_DRAFT_KEY);
        setTitle("");
        setFields([]);
      }
    } else {
      setTitle("");
      setFields([]);
    }

    try {
      const layoutRaw = localStorage.getItem(LAYOUT_KEY);
      if (layoutRaw) {
        const parsed = JSON.parse(layoutRaw);
        if (parsed?.panelSizes) setPanelSizes((prev) => ({ ...prev, ...parsed.panelSizes }));
        if (parsed?.collapsed) setCollapsed((prev) => ({ ...prev, ...parsed.collapsed }));
        if (typeof parsed?.cleanPreviewMode === "boolean") setCleanPreviewMode(parsed.cleanPreviewMode);
        if (parsed?.device === "mobile" || parsed?.device === "tablet") setDevice(parsed.device);
        if (typeof parsed?.showPropsInTablet === "boolean") setShowPropsInTablet(parsed.showPropsInTablet);
        if (Number.isFinite(parsed?.previewScrollTop)) setPreviewScrollTop(parsed.previewScrollTop);
      }
    } catch {}
  }, [formId]);

  // ---------- Load assets and folders for asset field configuration ----------
  useEffect(() => {
    if (!companyId) return;

    async function loadAssets() {
      try {
        const assetsRef = collection(db, `companies/${companyId}/assets`);
        const assetsSnap = await getDocs(assetsRef);
        const assetList = assetsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort by title
        assetList.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });
        setAssets(assetList);

        const foldersRef = collection(db, `companies/${companyId}/assetFolders`);
        const foldersSnap = await getDocs(foldersRef);
        const folderList = foldersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort by createdAt
        folderList.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return aTime - bTime;
        });
        setAssetFolders(folderList);
      } catch (err) {
        console.error('Failed to load assets:', err);
      }
    }

    loadAssets();
  }, [companyId]);

  // ---------- Persist layout + state ----------
  useEffect(() => {
    const payload = JSON.stringify({
      panelSizes,
      collapsed,
      cleanPreviewMode,
      device,
      showPropsInTablet,
      previewScrollTop,
    });
    localStorage.setItem(LAYOUT_KEY, payload);
  }, [panelSizes, collapsed, cleanPreviewMode, device, showPropsInTablet, previewScrollTop]);

  useEffect(() => {
    if (!previewScrollRef.current) return;
    previewScrollRef.current.scrollTop = previewScrollTop;
  }, [previewScrollTop, device]);

  useEffect(() => {
    if (!previewHostRef.current || typeof ResizeObserver === "undefined") return undefined;

    const node = previewHostRef.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setPreviewHostSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (formId) return;
    const payload = JSON.stringify({ title, fields });
    localStorage.setItem(FORM_DRAFT_KEY, payload);
  }, [title, fields, formId]);

  // ---------- Overlay cleanup ----------
  const overlayTimer = useRef(null);
  useEffect(() => () => overlayTimer.current && clearTimeout(overlayTimer.current), []);

  // ---------- Keyboard save (Ctrl/Cmd+S) ----------
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveForm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // --------- CRUD helpers ---------
  function addField(type, insertIndex = null) {
    const f = makeField(type, translate);
    setFields((p) => {
      if (insertIndex == null) return [...p, f];
      const cp = [...p];
      cp.splice(insertIndex, 0, f);
      return cp;
    });
    setSelectedId(f.id);
    return f;
  }

  // 🔁 Recursively update a field anywhere (supports nested sections)
  function updateField(id, patch) {
    setFields((p) =>
      p.map((f) => {
        if (f.id !== id) return f;

        const merged = {
          ...f,
          ...patch,
          config: { ...f.config, ...(patch.config || {}) },
          branchMap: { ...f.branchMap, ...(patch.branchMap || {}) },
          attachments: patch.attachments ?? f.attachments,
        };
        return merged;
      })
    );
  }



  // 🔁 Recursively update a field's config anywhere
function updateField(id, patch) {
  function recursiveUpdate(list) {
    return list.map((f) => {
      if (f.id === id) {
        // deep merge for branchMap, config, and attachments
        const merged = {
          ...f,
          ...patch,
          config: { ...f.config, ...(patch.config || {}) },
          branchMap: { ...f.branchMap, ...(patch.branchMap || {}) },
          attachments: patch.attachments ?? f.attachments,
        };
        return merged;
      }

      // keep traversing nested children
      if (f.children?.length) {
        return { ...f, children: recursiveUpdate(f.children) };
      }
      return f;
    });
  }

  setFields((prev) => recursiveUpdate(prev));
}




// 🔁 Recursively update config only
function updateFieldConfig(id, patch) {
  function recursiveUpdate(list) {
    return list.map((f) => {
      if (f.id === id) {
        return { ...f, config: { ...f.config, ...patch } };
      }
      if (f.children?.length) {
        return { ...f, children: recursiveUpdate(f.children) };
      }
      return f;
    });
  }
  setFields((prev) => recursiveUpdate(prev));
}


  function ensureBranchEntry(sourceId, groupKey) {
    setFields((p) =>
      p.map((f) => {
        if (f.id !== sourceId) return f;
        const bm = { ...(f.branchMap || {}) };
        if (!bm[groupKey]) bm[groupKey] = { notify: false, targets: [] };
        return { ...f, branchMap: bm };
      })
    );
  }
  
  function toggleBranchNotify(sourceId, groupKey, val) {
    setFields((p) =>
      p.map((f) => {
        if (f.id !== sourceId) return f;
        const bm = { ...(f.branchMap || {}) };
        const g = bm[groupKey] || { notify: false, targets: [] };
        g.notify = val;
        bm[groupKey] = g;
        return { ...f, branchMap: bm };
      })
    );
  }
  function addBranchTarget(sourceId, groupKey, targetId) {
  setFields((prev) => {
    const updateBranch = (list) =>
      list.map((f) => {
        // ✅ Source field inside this level
        if (f.id === sourceId) {
          const bm = { ...(f.branchMap || {}) };
          const g = bm[groupKey] || { notify: false, targets: [] };
          if (!g.targets.includes(targetId))
            g.targets = [...g.targets, targetId];
          bm[groupKey] = g;
          return { ...f, branchMap: bm };
        }

        // ✅ Recurse into children if they exist
        if (f.children?.length) {
          return { ...f, children: updateBranch(f.children) };
        }

        return f;
      });

    return updateBranch(prev);
  });
}

  function removeBranchTarget(sourceId, groupKey, targetId) {
    setFields((p) =>
      p.map((f) => {
        if (f.id !== sourceId) return f;
        const bm = { ...(f.branchMap || {}) };
        const g = bm[groupKey];
        if (!g) return f;
        g.targets = (g.targets || []).filter((id) => id !== targetId);
        bm[groupKey] = g;
        return { ...f, branchMap: bm };
      })
    );
  }
  function deleteField(id) {
    setFields((p) => p.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
    setPreviewValues((pv) => {
      const cp = { ...pv };
      delete cp[id];
      return cp;
    });
    // Remove from branch targets
    setFields((p) =>
      p.map((f) => {
        if (!f.branchMap) return f;
        const bm = { ...f.branchMap };
        let changed = false;
        for (const k of Object.keys(bm)) {
          const nt = (bm[k].targets || []).filter((tid) => tid !== id);
          if (nt.length !== (bm[k].targets || []).length) {
            bm[k] = { ...bm[k], targets: nt };
            changed = true;
          }
        }
        return changed ? { ...f, branchMap: bm } : f;
      })
    );
  }
  function duplicateField(id) {
    setFields((p) => {
      const idx = p.findIndex((f) => f.id === id);
      if (idx === -1) return p;
      const copy = JSON.parse(JSON.stringify(p[idx]));
      copy.id = nanoid();
      if (copy?.config?.options) {
        copy.config.options = copy.config.options.map((o) => ({ ...o, id: nanoid() }));
      }
      const next = [...p];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }

  // --------- Move field helpers (up / down) ---------
  function moveField(id, delta) {
    // Try top-level first
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === id);
      if (idx !== -1) {
        const to = idx + delta;
        if (to < 0 || to >= prev.length) return prev;
        const next = [...prev];
        const tmp = next[to];
        next[to] = next[idx];
        next[idx] = tmp;
        return next;
      }

      // Not top-level: try to find within sections (children or instances)
      function recursive(list) {
        return list.map((f) => {
          if (f.children?.length) {
            const ci = f.children.findIndex((c) => c.id === id);
            if (ci !== -1) {
              const to = ci + delta;
              if (to < 0 || to >= f.children.length) return f; // can't move
              const nc = [...f.children];
              const tmp = nc[to];
              nc[to] = nc[ci];
              nc[ci] = tmp;
              return { ...f, children: nc };
            }
            // recurse into children
            return { ...f, children: recursive(f.children) };
          }

          // repeatable instances
          if (f.instances?.length) {
            const inst = f.instances.find((inst) => inst.children?.some((c) => c.id === id));
            if (inst) {
              const ci = inst.children.findIndex((c) => c.id === id);
              const to = ci + delta;
              if (to < 0 || to >= inst.children.length) return f;
              const newInstances = f.instances.map((ins) => {
                if (ins.id !== inst.id) return ins;
                const nc = [...ins.children];
                const tmp = nc[to];
                nc[to] = nc[ci];
                nc[ci] = tmp;
                return { ...ins, children: nc };
              });
              return { ...f, instances: newInstances };
            }
          }

          return f;
        });
      }

      return recursive(prev);
    });
  }

  function moveFieldUp(id) {
    moveField(id, -1);
  }
  function moveFieldDown(id) {
    moveField(id, 1);
  }

  // -------------------- Section nesting helpers --------------------
function addFieldToSection(fields, sectionId, newField) {
  const next = [];
  for (const f of fields) {
    if (f.id === sectionId) {
      const children = f.children || [];
      next.push({ ...f, children: [...children, newField] });
    } else {
      // ensure we don't keep the same newField at top level
      if (f.id !== newField.id) next.push(f);
    }
  }
  return next;
}

function flattenSplitWorkflowFieldsForBuilder(list = []) {
  const flattened = [];

  for (const field of list) {
    if (!field) continue;

    if (field.type === SPLIT_WORKFLOW_TYPE) {
      const children = Array.isArray(field.children) ? field.children : [];
      flattened.push({
        ...field,
        children: [],
        instances: field.instances || [],
      });
      flattened.push(...flattenSplitWorkflowFieldsForBuilder(children));
      continue;
    }

    flattened.push({
      ...field,
      children: Array.isArray(field.children)
        ? flattenSplitWorkflowFieldsForBuilder(field.children)
        : field.children,
    });
  }

  return flattened;
}

function materializeSplitWorkflowFieldsForPersist(list = []) {
  const materialized = [];

  for (let i = 0; i < list.length; i += 1) {
    const field = list[i];
    if (!field) continue;

    if (field.type === SPLIT_WORKFLOW_TYPE) {
      const workflowChildren = [];
      let cursor = i + 1;

      while (cursor < list.length && list[cursor]?.type !== SPLIT_WORKFLOW_TYPE) {
        workflowChildren.push(list[cursor]);
        cursor += 1;
      }

      materialized.push({
        ...field,
        children: materializeSplitWorkflowFieldsForPersist(workflowChildren),
      });
      i = cursor - 1;
      continue;
    }

    materialized.push({
      ...field,
      children: Array.isArray(field.children)
        ? materializeSplitWorkflowFieldsForPersist(field.children)
        : field.children,
    });
  }

  return materialized;
}

function sanitizeFirestoreValue(value) {
  if (value === undefined) return undefined;

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeFirestoreValue(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === "object" && value.constructor === Object) {
    return Object.entries(value).reduce((acc, [key, entryValue]) => {
      const sanitized = sanitizeFirestoreValue(entryValue);
      if (sanitized !== undefined) {
        acc[key] = sanitized;
      }
      return acc;
    }, {});
  }

  return value;
}



  // --------- Attachments handlers ---------
  async function onAddAttachment(fieldId, file) {
    if (!file || !companyId) return;
    try {
      setUploadingField(fieldId);
      const info = await uploadToStorage(companyId, fieldId, file);
      setFields((p) => {
        function recursiveAdd(list) {
          return list.map((f) => {
            if (f.id === fieldId) {
              return { ...f, attachments: [...(f.attachments || []), info] };
            }
            if (f.children?.length) {
              return { ...f, children: recursiveAdd(f.children) };
            }
            return f;
          });
        }
        return recursiveAdd(p);
      });
    } catch (e) {
      console.error(e);
      alert("Failed to upload file.");
    } finally {
      setUploadingField(null);
    }
  }
  
  function onRemoveAttachment(fieldId, url) {
    setFields((p) => {
      function recursiveRemove(list) {
        return list.map((f) => {
          if (f.id === fieldId) {
            return { ...f, attachments: (f.attachments || []).filter((a) => a.url !== url) };
          }
          if (f.children?.length) {
            return { ...f, children: recursiveRemove(f.children) };
          }
          return f;
        });
      }
      return recursiveRemove(p);
    });
  }

  // 🔍 Memoized recursive field finder (safe for hooks)
const findFieldById = useCallback((id, list = fields) => {
  for (const f of list) {
    if (f.id === id) return f;
    if (f.children?.length) {
      const found = findFieldById(id, f.children);
      if (found) return found;
    }
    // ✅ Also search through repeatable section instances
    if (f.instances?.length) {
      for (const inst of f.instances) {
        if (inst.id === id) return inst;
        if (inst.children?.length) {
          const found = findFieldById(id, inst.children);
          if (found) return found;
        }
      }
    }
  }
  return null;
}, [fields]);


// 🧭 Determines if a field is linked (as source or target) to the currently selected field
const isLinkedToSelected = useCallback(
  (field) => {
    if (!selectedId) return false;

    const selectedField = findFieldById(selectedId, fields);
    if (!selectedField) return false;

    // If the selected field targets this one
    for (const group of Object.values(selectedField.branchMap || {})) {
      if (group.targets?.includes(field.id)) return true;
    }

    // If this field itself targets the selected one
    for (const group of Object.values(field.branchMap || {})) {
      if (group.targets?.includes(selectedId)) return true;
    }

    return false;
  },
  [selectedId, fields, findFieldById]
);



function serializeFields(fields, previewValues) {
  return fields.map((f) => {
    // 🟦 Handle repeatable section
    if (isWorkflowSection(f) && f.config?.repeatable) {
      const instances = f.instances || [f];
      return {
        id: f.id,
        label: f.label,
        type: f.type,
        repeatable: true,
        instances: instances.map((inst) => {
          const childData = serializeFields(inst.children || [], previewValues);
          const obj = {};
          for (const c of childData) {
            if (c.value !== undefined) obj[c.id] = c.value;
          }
          return obj;
        }),
      };
    }

    // 🧩 Normal field
    if (isWorkflowSection(f)) {
      // Non-repeatable section
      return {
        id: f.id,
        label: f.label,
        type: f.type,
        children: serializeFields(f.children || [], previewValues),
      };
    }

    // Regular simple field
    return {
      id: f.id,
      label: f.label,
      type: f.type,
      value: previewValues[f.id],
    };
  });
}


  // --------- DnD handlers ---------
function onDragEnd(e) {
  const { active, over } = e;
  if (!over) return;

  const aData = active?.data?.current || {};
  const overId = String(over.id);

  // 1️⃣ Toolbox drag: adding a new field
  if (aData.from === "toolbox") {
    const type = aData.fieldType;
    const newField = makeField(type, translate);

    // 🟦 Dropped inside a section container
    if (overId.startsWith("section-drop:")) {
      // Prevent nesting sections within sections
      if (isWorkflowSection({ type })) {
        alert("Sections cannot be placed inside other sections.");
        return;
      }
      const sectionId = overId.split(":")[1];
      setFields((prev) => addFieldToSection(prev, sectionId, newField));
      setSelectedId(newField.id);
      return;
    }

    // 🟥 Dropped into a branch area
    if (overId.startsWith("branch-drop:")) {
      const parts = overId.split(":");
      const sourceId = parts[1];
      const groupKey = parts.slice(2).join(":");
      if (!sourceId || !groupKey) return;
      ensureBranchEntry(sourceId, groupKey);

      // 🧠 Check if source is inside a section
      const sourceField = findFieldById(sourceId);
      const parentSection = fields.find((f) =>
        (f.children || []).some((c) => c.id === sourceId)
      );

      if (parentSection) {
        // ✅ Add new branch target inside the same section
        setFields((prev) =>
          prev.map((f) =>
            f.id === parentSection.id
              ? {
                  ...f,
                  children: [...(f.children || []), newField],
                }
              : f
          )
        );
      } else {
        // 🟢 Add to top-level otherwise
        setFields((p) => [...p, newField]);
      }

      addBranchTarget(sourceId, groupKey, newField.id);
      setSelectedId(newField.id);
      return;
    }

    // 🟨 Dropped on a canvas insert bar
    if (overId.startsWith("canvas-insert:")) {
      const [, idxStr] = overId.split(":");
      const insertIndex = Math.max(0, Math.min(fields.length, Number(idxStr)));
      setFields((p) => {
        const cp = [...p];
        cp.splice(insertIndex, 0, newField);
        return cp;
      });
      setSelectedId(newField.id);
      return;
    }

    const overFieldIndex = fields.findIndex((field) => field.id === overId);
    if (overFieldIndex !== -1) {
      setFields((p) => {
        const cp = [...p];
        cp.splice(overFieldIndex + 1, 0, newField);
        return cp;
      });
      setSelectedId(newField.id);
      return;
    }

    // 🟩 Dropped on top of the canvas
    if (overId === "canvas-drop") {
      setFields((p) => [newField, ...p]);
      setSelectedId(newField.id);
      return;
    }
  }

  // 2️⃣ Reordering existing fields (same level)
  const activeId = active.id;
  const overIdRaw = over.id;
  const activeIndex = fields.findIndex((f) => f.id === activeId);
  const overIndex = fields.findIndex((f) => f.id === overIdRaw);

  if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
    setFields((items) => arrayMove(items, activeIndex, overIndex));
  }
}


function serializeFields(fields, previewValues) {
  return fields.map((f) => {
    // 🟦 Repeatable section
    if (isWorkflowSection(f) && f.config?.repeatable) {
      const instances = f.instances || [f];
      return {
        id: f.id,
        label: f.label,
        type: f.type,
        repeatable: true,
        instances: instances.map((inst) => {
          const childData = serializeFields(inst.children || [], previewValues);
          const obj = {};
          for (const c of childData) {
            if (c.value !== undefined) obj[c.id] = c.value;
          }
          return obj;
        }),
      };
    }

    // 🧩 Non-repeatable section
    if (isWorkflowSection(f)) {
      return {
        id: f.id,
        label: f.label,
        type: f.type,
        children: serializeFields(f.children || [], previewValues),
      };
    }

    // 🟢 Simple field
    return {
      id: f.id,
      label: f.label,
      type: f.type,
      value: previewValues[f.id],
    };
  });
}


  // ---------- Save form (create or update) ----------
async function saveForm() {
  if (!title.trim()) return alert("Please enter a form title.");
  if (fields.length === 0) return alert("Add at least one field.");
  if (!companyId) {
    const userCompanyId = await getCurrentUserCompanyId();
    if (!userCompanyId) {
      return alert("❌ No company assigned to your account");
    }
    setCompanyId(userCompanyId);
  }
  setSaving(true);

  try {
    const persistedFields = materializeSplitWorkflowFieldsForPersist(fields);
    const sanitizedPersistedFields = sanitizeFirestoreValue(persistedFields);
    const targetCompanyId = companyId || await getCurrentUserCompanyId();
    const currentUserData = await getCurrentUserData();
    const currentUser = {
      uid: currentUserData?.uid || "unknown",
      displayName: currentUserData?.fullName || currentUserData?.displayName || null,
      fullName: currentUserData?.fullName || null,
      email: currentUserData?.email || null,
    };
    // Check permission for creating new forms. Editing existing forms is allowed here;
    // only block when creating a new form and the user lacks create_forms.
    if (!formId) {
      if (!hasPermission(currentUserData, 'create_forms')) {
        alert('You do not have permission to create forms.');
        setSaving(false);
        return;
      }
    }

    if (formId) {
      // ✅ update existing form
        const payload = sanitizeFirestoreValue({
          title,
          fields: sanitizedPersistedFields,
          folderId: selectedFolderId || null,
          assignedUsers,
          assignedGroups,
          allUsers,
          allGroups,
          updatedAt: serverTimestamp(),
          lastEditedVia: "web",
          allowMobileEdit: false,
          source: "web",
          ...buildUpdatedDocumentControl({
            existingForm: originalForm || { createdBy: currentUser.uid, createdByName: currentUser.fullName },
            user: currentUser,
            platform: "web",
            summary: revisionSummary.trim(),
            comments: revisionComments.trim(),
          }),
        });
        await setDoc(
          doc(db, `companies/${targetCompanyId}/forms/${formId}`),
          payload,
        { merge: true }
      );
    } else {
      // ✅ create new form
      const payload = sanitizeFirestoreValue({
        title,
        fields: sanitizedPersistedFields,
        folderId: selectedFolderId || null,
        assignedUsers,
        assignedGroups,
        allUsers,
        allGroups,
        archived: false,
        createdAt: serverTimestamp(), // ✅ only set once when created
        updatedAt: serverTimestamp(), // ✅ initial timestamp for sorting
        createdVia: "web",
        lastEditedVia: "web",
        allowMobileEdit: false,
        source: "web",
        createdBy: currentUser.uid,
        createdByName: currentUser.fullName || currentUser.email || "Unknown user",
        ...buildNewDocumentControl({
          user: currentUser,
          platform: "web",
          summary: revisionSummary.trim(),
          comments: revisionComments.trim(),
        }),
      });
      await addDoc(collection(db, `companies/${targetCompanyId}/forms`), payload);

      localStorage.removeItem(FORM_DRAFT_KEY); // ✅ clear old draft
    }

    

    // ✅ local instant update for UI timestamp display
    setUpdatedAt(new Date().toISOString());
    setRevisionSummary("");
    setRevisionComments("");

    alert("✅ Form saved!");
    if (onBack) onBack();
  } catch (e) {
    console.error("❌ Error saving form:", e);
    alert("❌ Failed to save form.");
  } finally {
    setSaving(false);
  }
}

function exportJSON() {
  try {
      const data = serializeFields(fields, previewValues);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${title || "form"}.json`;
    a.click();
    URL.revokeObjectURL(url);

    console.log("✅ Exported JSON:", data);
  } catch (err) {
    console.error("Export failed:", err);
    alert("Failed to export JSON.");
  }
}


  // ---------- Visibility & value setters (ESLint-safe) ----------
  const isFieldVisibleFn = useMemo(() => computeVisibility(fields, previewValues), [fields, previewValues]);
  const isVisible = useCallback((id) => isFieldVisibleFn(id), [isFieldVisibleFn]);
  const setValueFor = useCallback((id, val) => setPreviewValues((p) => ({ ...p, [id]: val })), []);
  const openViewer = useCallback((url) => {
    // Open image in new tab or modal
    // For now, just open in new tab
    if (url) {
      window.open(url, '_blank');
    }
  }, []);

  // ---------- Resizing logic ----------
  const dragRef = useRef({ active: false, target: null, startX: 0, startW: 0 });

  function startResize(target, e) {
    e.preventDefault();
    const currentW =
      target === "toolbox"
        ? panelSizes.toolbox
        : target === "canvas"
        ? panelSizes.canvas
        : panelSizes.props;
    dragRef.current = { active: true, target, startX: e.clientX, startW: currentW };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
  }
  function onDrag(e) {
    if (!dragRef.current.active) return;
    const { target, startX, startW } = dragRef.current;
    const dx = e.clientX - startX;
    let next = startW + dx;

    if (target === "toolbox") next = clamp(next, minW.toolbox, maxW.toolbox);
    if (target === "canvas") next = clamp(next, minW.canvas, maxW.canvas);
    if (target === "props") next = clamp(next, minW.props, maxW.props);

    if (next <= minW[target]) next = minW[target];

    setPanelSizes((p) => {
      const updated = { ...p, [target]: next };
      setCollapsed((c) => {
        if (c[target] && next > minW[target] + 5) {
          return { ...c, [target]: false };
        }
        return c;
      });
      return updated;
    });
  }
  function stopDrag() {
    dragRef.current = { active: false, target: null, startX: 0, startW: 0 };
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("mouseup", stopDrag);
  }

  // Collapse toggles
  const toggleCollapse = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  const expandAll = () => setCollapsed({ toolbox: false, canvas: false, props: false, preview: false });
  const resetPreviewLayout = () => {
    setDevice("mobile");
    setShowPropsInTablet(true);
    setCleanPreviewMode(true);
    setPreviewScrollTop(0);
    setSelectedId(null);
    setCollapsed((current) => ({ ...current, preview: false }));
    if (previewScrollRef.current) {
      previewScrollRef.current.scrollTop = 0;
    }
    try {
      const layoutRaw = localStorage.getItem(LAYOUT_KEY);
      if (!layoutRaw) return;
      const parsed = JSON.parse(layoutRaw);
      localStorage.setItem(LAYOUT_KEY, JSON.stringify({
        ...parsed,
        cleanPreviewMode: true,
        device: "mobile",
        showPropsInTablet: true,
        previewScrollTop: 0,
        collapsed: {
          ...(parsed?.collapsed || {}),
          preview: false,
        },
      }));
    } catch {
      // Ignore malformed saved layout and let the persistence effect recreate it.
    }
  };

  // Computed styles for panels (flex layout)
  const toolboxStyle = collapsed.toolbox
    ? { width: 0, flex: "0 0 0" }
    : { width: panelSizes.toolbox, flex: "0 0 auto" };
  const canvasStyle = collapsed.canvas
    ? { width: 0, flex: "0 0 0" }
    : { width: panelSizes.canvas, flex: "0 0 auto" };
  const propsStyle = collapsed.props
    ? { width: 0, flex: "0 0 0" }
    : { width: panelSizes.props, flex: "0 0 auto" };
  const previewStyle = collapsed.preview
    ? { flex: "0 0 0", width: 0, minWidth: 0 }
    : { flex: "1 1 0", minWidth: panelSizes.previewMin };
  const previewDeviceConfig = device === "mobile"
    ? { frameWidth: 340, frameHeight: 680, screenWidth: 310, screenHeight: 620 }
    : { frameWidth: 560, frameHeight: 760, screenWidth: 512, screenHeight: 696 };
  const previewScale = useMemo(() => {
    if (!previewHostSize.width || !previewHostSize.height) return 1;

    const availableWidth = Math.max(previewHostSize.width - 24, 220);
    const availableHeight = Math.max(previewHostSize.height - 24, 220);
    const widthScale = availableWidth / previewDeviceConfig.frameWidth;
    const heightScale = availableHeight / previewDeviceConfig.frameHeight;

    return Math.max(0.5, Math.min(widthScale, heightScale, 1));
  }, [previewDeviceConfig.frameHeight, previewDeviceConfig.frameWidth, previewHostSize.height, previewHostSize.width]);
  const isCleanPreviewActive = cleanPreviewMode;

  // ---------- Export / Import JSON ----------
  function exportJSON() {
    const payload = { title, fields };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "form").replace(/\\s+/g, "_")}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  function importJSON(file) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (data?.title) setTitle(data.title);
        if (Array.isArray(data?.fields)) {
          setFields(flattenSplitWorkflowFieldsForBuilder(data.fields));
        }
        alert("✅ Imported form JSON");
      } catch {
        alert("❌ Invalid JSON file");
      }
    };
    reader.readAsText(file);
  }

  const selected = useMemo(
  () => findFieldById(selectedId, fields) || null,
  [selectedId, fields, findFieldById]
);


  const branchIndex = useMemo(() => buildBranchIndex(fields), [fields]);

  // ... other logic and helper functions like saveForm(), addField(), etc.

function hasUnsavedChanges() {
  try {
    // If editing an existing form
    if (formId && originalForm) {
      // Compare current in-memory title and fields with original
      const current = JSON.stringify({ title, fields });
      const original = JSON.stringify({
        title: originalForm.title || "",
        fields: originalForm.fields || [],
      });
      return current !== original;
    }

    // For new forms, check localStorage draft
    const draft = JSON.parse(localStorage.getItem("bmc:builderDraft:v1"));
    const hasTitle = title.trim().length > 0;
    const hasFields = fields.length > 0;
    const hasDraft =
      draft && (draft.title?.trim()?.length > 0 || draft.fields?.length > 0);

    return hasTitle || hasFields || hasDraft;
  } catch (err) {
    console.error("Error checking unsaved changes:", err);
    return false;
  }
}



  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => {
        const ft = e?.active?.data?.current?.fieldType;
        if (ft) {
          setActiveToolLabel(labelFor(ft, translate));
          overlayTimer.current = setTimeout(() => setActiveToolLabel(""), 1500);
        }
      }}
      onDragEnd={onDragEnd}
    >
      {/* FLEX LAYOUT with handles */}
      <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white/90 transition-colors">
  {/* -------------------- Toolbox Panel -------------------- */}
<motion.aside
  initial={false}
  animate={{ opacity: collapsed.toolbox ? 0 : 1 }}
  transition={{ duration: 0.18 }}
  style={toolboxStyle}
  className="border-r border-white/10 bg-white/5 backdrop-blur-xl p-4 overflow-auto relative transition-colors text-white"
>
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <img src={logo} alt="BMC" className="w-9 h-9" />
      <h3 className="text-white font-semibold m-0">BuildMyChecks</h3>
    </div>
    <button
      onClick={() => toggleCollapse("toolbox")}
      className="text-xs px-2 py-1 border border-white/20 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
      title="Collapse toolbox"
    >
      ⟨
    </button>
  </div>

  {/* 🔍 Search bar for filtering field types */}
  <input
    type="text"
    value={toolSearch || ""}
    onChange={(e) => setToolSearch(e.target.value)}
    placeholder={translate('formBuilder.searchFieldTypes')}
    className="w-full mb-3 px-3 py-2 text-sm rounded-md border border-white/15 bg-white/10 text-white placeholder-white/60"
  />

  <p className="text-xs text-white/70 m-0 mb-2">
    {translate('formBuilder.clickOrDragToAdd')}
  </p>

  {/* Field type list */}
  <div className="grid gap-2">
    {(() => {
      const matchesSearch = (label) =>
        !toolSearch || label.toLowerCase().includes(toolSearch.toLowerCase());

      const renderToolboxOption = (ft) => (
        <div
          key={ft.type}
          className={`grid grid-cols-[1fr_auto] gap-2 items-center ${
            isWorkflowSection(ft)
              ? "bg-blue-500/15 border border-blue-300/40 rounded-xl p-2"
              : "border border-white/10 rounded-xl p-2 bg-white/5 hover:bg-white/10"
          }`}
        >
          <DraggableToolboxItem type={ft.type} label={ft.label} />
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => addField(ft.type)}
            className="px-2 py-2 text-xs border border-white/15 rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors"
            title={translate('formBuilder.addToCanvas')}
          >
            {translate('formBuilder.add')}
          </motion.button>
        </div>
      );

      const sectionField = FIELD_TYPES.find((ft) => ft.type === "section");
      const splitWorkflowField = FIELD_TYPES.find((ft) => ft.type === SPLIT_WORKFLOW_TYPE);
      const baseFields = FIELD_TYPES.filter(
        (ft) => ft.type !== "section" && ft.type !== SPLIT_WORKFLOW_TYPE
      ).map((ft) => ({ ...ft, label: translate(ft.labelKey) }));

      const orderedFields = [
        ...baseFields,
        sectionField ? { ...sectionField, label: translate(sectionField.labelKey) } : null,
        splitWorkflowField ? { ...splitWorkflowField, label: translate(splitWorkflowField.labelKey) } : null,
      ].filter(Boolean);

      return orderedFields
        .filter((ft) => matchesSearch(ft.label))
        .map(renderToolboxOption);
    })()}
  </div>

  {/* Footer */}
  <div className="mt-4 flex items-center gap-2">
    <motion.button
      onClick={() => {
        if (hasUnsavedChanges()) {
          const confirmLeave = window.confirm(
            translate('formBuilder.unsavedChanges')
          );
          if (!confirmLeave) return;
        }
        onBack();
      }}
      className="flex-1 border border-white/15 rounded-md px-3 py-2 font-semibold bg-white/10 hover:bg-white/20 text-white transition-colors"
    >
      ← {translate('formBuilder.back')}
    </motion.button>
  </div>
</motion.aside>

{/* Handle: Toolbox | Canvas */}
<ResizeHandle onMouseDown={(e) => startResize("toolbox", e)} />


{/* -------------------- Canvas Panel -------------------- */}
<motion.main
  initial={false}
  animate={{ opacity: collapsed.canvas ? 0 : 1 }}
  transition={{ duration: 0.18 }}
  style={canvasStyle}
  className="p-5 overflow-auto bg-white/5 backdrop-blur-xl border-x border-white/10 transition-colors"
>
  {/* Tab Navigation */}
  <div className="flex gap-2 mb-4">
    <button
      onClick={() => setActiveBuilderTab("fields")}
      className={`px-4 py-2 rounded-lg font-semibold transition-all ${
        activeBuilderTab === "fields"
          ? "bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white shadow-lg shadow-fuchsia-900/30"
          : "border border-white/15 bg-white/5 text-white/80 hover:bg-white/10"
      }`}
    >
      📝 Fields
    </button>
    <button
      onClick={() => setActiveBuilderTab("schedules")}
       className={`px-4 py-2 rounded-lg font-semibold transition-all ${
         activeBuilderTab === "schedules"
           ? "bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white shadow-lg shadow-fuchsia-900/30"
           : "border border-white/15 bg-white/5 text-white/80 hover:bg-white/10"
       }`}
    >
      📅 Schedules
    </button>
    <button
      onClick={() => setActiveBuilderTab("approvals")}
      className={`px-4 py-2 rounded-lg font-semibold transition-all ${
        activeBuilderTab === "approvals"
          ? "bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white shadow-lg shadow-fuchsia-900/30"
          : "border border-white/15 bg-white/5 text-white/80 hover:bg-white/10"
      }`}
    >
      ✅ Approvals
    </button>

  </div>
  <div className="grid gap-3 mb-4 md:grid-cols-2">
    <input
      value={revisionSummary}
      onChange={(e) => setRevisionSummary(e.target.value)}
      placeholder={formId ? "What changed in this revision?" : "Initial issue summary"}
      className="bg-white/10 border border-white/15 text-white rounded-lg px-3 py-3 transition-colors placeholder-white/50"
    />
    <input
      value={revisionComments}
      onChange={(e) => setRevisionComments(e.target.value)}
      placeholder="Optional comments for the audit trail"
      className="bg-white/10 border border-white/15 text-white rounded-lg px-3 py-3 transition-colors placeholder-white/50"
    />
  </div>

  {activeBuilderTab === "fields" ? (
    <>
  <div className="flex flex-wrap gap-2 items-center mb-3">
    <input
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      placeholder={translate('formBuilder.formTitle')}
      className="flex-1 bg-white/10 border border-white/15 text-white rounded-lg px-3 py-2 transition-colors placeholder-white/60"
    />
    <select
      value={selectedFolderId || ""}
      onChange={(e) => setSelectedFolderId(e.target.value || null)}
      className="bg-white/10 border border-white/15 text-white rounded-lg px-3 py-2 transition-colors"
    >
      <option value="">📁 {translate('formBuilder.noFolder')}</option>
      {folders.map(folder => (
        <option key={folder.id} value={folder.id}>
          📁 {folder.name}
        </option>
      ))}
    </select>
    <div className="min-w-[220px] rounded-lg border border-cyan-300/20 bg-cyan-500/10 px-3 py-2 text-xs text-white/80">
      <div className="font-semibold uppercase tracking-wider text-cyan-100/80">Document Control</div>
      <div className="mt-1">Next revision: {`Rev ${formId ? getCurrentRevision(originalForm) + 1 : 1}`}</div>
    </div>
    {formId && lastEditedDate && (
      <p className="text-xs text-white/70 ml-1">
        {translate('formBuilder.lastEdited')}{" "}
        <span className="font-medium">
          {lastEditedDate.toLocaleDateString()} {lastEditedDate.toLocaleTimeString()}
        </span>
      </p>
    )}
    {/* Device toggle moved into the live preview header */}
    <motion.button
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      onClick={saveForm}
      disabled={saving}
      className={`px-4 py-2 rounded-md text-white font-semibold shadow-lg ${
        saving
          ? "bg-white/20 cursor-not-allowed"
          : "bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 hover:brightness-110"
      }`}
    >
      {saving ? translate('formBuilder.saving') : `💾 ${translate('formBuilder.save')}`}
    </motion.button>

    {/* Export / Import / Layout controls */}
    <motion.button
  whileHover={{ y: -1 }}
  whileTap={{ scale: 0.98 }}
  onClick={exportJSON}
  className="px-3 py-2 rounded-md border border-white/15 bg-white/10 hover:bg-white/20 text-white transition-colors"
  title="Export JSON"
>
  ⭳ Export
</motion.button>

    <label className="px-3 py-2 rounded-md border border-white/15 bg-white/10 hover:bg-white/20 cursor-pointer text-white transition-colors">
      ⭱ Import
      <input
        type="file"
        accept="application/json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) importJSON(file);
        }}
      />
    </label>
    <motion.button
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => {
        setPanelSizes({ toolbox: 280, canvas: 660, props: 380, previewMin: 420 });
        setCollapsed({ toolbox: false, canvas: false, props: false, preview: false });
      }}
      className="px-3 py-2 rounded-md border border-white/15 bg-white/10 hover:bg-white/20 text-white transition-colors"
      title={translate('formBuilder.resetLayout')}
    >
      ↺ {translate('formBuilder.resetLayout')}
    </motion.button>
    <motion.button
  whileHover={{ y: -1 }}
  whileTap={{ scale: 0.98 }}
  onClick={toggleDarkMode}
  className="px-3 py-2 rounded-md border border-white/15 bg-white/10 hover:bg-white/20 text-white transition-colors"
  title={translate('formBuilder.toggleDarkMode')}
>
  🌙 / ☀️
</motion.button>

    <motion.button
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      onClick={expandAll}
      className="px-3 py-2 rounded-md border border-white/15 bg-white/10 hover:bg-white/20 text-white transition-colors"
      title={translate('formBuilder.expandAllPanels')}
    >
      ⤢ {translate('formBuilder.expandAll')}
    </motion.button>

    <button
      onClick={() => toggleCollapse("canvas")}
      className="text-xs px-2 py-1 border border-white/20 rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors ml-2"
      title={translate('formBuilder.collapseCanvas')}
    >
      ⟨
    </button>
  </div>

  {/* Access Control Section */}
  <div className="mb-4">
    <AccessAssignment
      assignedUsers={assignedUsers}
      assignedGroups={assignedGroups}
      allUsers={allUsers}
      allGroups={allGroups}
      onChange={({ assignedUsers: newUsers, assignedGroups: newGroups, allUsers: newAllUsers, allGroups: newAllGroups }) => {
        setAssignedUsers(newUsers);
        setAssignedGroups(newGroups);
        setAllUsers(newAllUsers);
        setAllGroups(newAllGroups);
      }}
    />
  </div>

  <div className="text-sm text-white/70 mb-2">{translate('formBuilder.formCanvas')}</div>

  <CanvasDropArea>{translate('formBuilder.dropHereToAddAtTop')}</CanvasDropArea>

  <div className="bg-white/5 border border-white/10 rounded-2xl p-3 transition-colors backdrop-blur-xl">
  <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
    <AnimatePresence initial={false}>
      {fields.length === 0 ? (
        <motion.div
          key="empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-white/70 text-center py-8"
        >
          {translate('formBuilder.clickOrDragFromLeft')}
        </motion.div>
      ) : (
        (() => {
          // Helper to get all branch target IDs
          const getAllBranchTargets = () => {
            const targets = new Set();
            fields.forEach(f => {
              if (f.branchMap) {
                Object.values(f.branchMap).forEach(branch => {
                  if (branch.targets) {
                    branch.targets.forEach(tid => targets.add(tid));
                  }
                });
              }
            });
            return targets;
          };

          const branchTargets = getAllBranchTargets();

          // Helper to render branch children of a field
          const renderBranchChildren = (parentField) => {
            if (!parentField.branchMap) return null;

            const children = [];
            Object.values(parentField.branchMap).forEach(branch => {
              if (branch.targets) {
                branch.targets.forEach(tid => {
                  const childField = fields.find(f => f.id === tid);
                  if (childField) {
                    children.push(
                      <div key={childField.id} className="ml-8 mt-2">
                        <SortableItem
                          id={childField.id}
                          field={childField}
                          selected={selectedId === childField.id}
                          onSelect={setSelectedId}
                          visualDepth={0}
                          isBranched={true}
                          onDuplicate={duplicateField}
                        />
                        <div className="flex gap-2 -mt-1 mb-3 ml-2">
                          <motion.button
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setSelectedId(childField.id)}
                            className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                          >
                            ✏️ {translate('formBuilder.edit')}
                          </motion.button>
                          <motion.button
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => deleteField(childField.id)}
                            className="border border-white/35 bg-white/85 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/95 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                          >
                            🗑 {translate('formBuilder.delete')}
                          </motion.button>
                        </div>
                        {/* Recursively render this child's branch children */}
                        {renderBranchChildren(childField)}
                      </div>
                    );
                  }
                });
              }
            });

            return children.length > 0 ? <div className="border-l-2 border-rose-300 ml-4">{children}</div> : null;
          };

          // Render only top-level fields (not branch targets)
          return fields
            .filter(f => !branchTargets.has(f.id))
            .map((f, idx) => {
              const topLevelIndex = fields.filter(field => !branchTargets.has(field.id)).indexOf(f);
              
               return f.type === SPLIT_WORKFLOW_TYPE ? (
                <div key={f.id}>
                  <CanvasInsertBar id={`canvas-insert:${idx}`} />

                  <div
                    onClick={() => setSelectedId(f.id)}
                    className={`mb-3 rounded-2xl border-2 p-4 shadow-sm cursor-pointer transition-all ${
                      selectedId === f.id
                        ? "border-amber-500 bg-amber-100/80 dark:bg-amber-900/30 ring-2 ring-amber-400/60"
                        : "border-amber-300 bg-amber-50/60 dark:bg-amber-900/20 hover:border-amber-400"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-amber-700 dark:text-amber-300">
                          🔀 {f.label || translate('formBuilder.fieldSplitWorkflow')}
                        </div>
                        <div className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
                          Fields dropped below this marker stay in the normal canvas flow and belong to this workflow section until the next split workflow.
                        </div>
                      </div>
                      <div className="text-xs text-amber-700 dark:text-amber-300 whitespace-nowrap">
                        {f.config?.previousSectionVisibility === "hidden" ? "Previous hidden" : "Previous visible"}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 -mt-1 mb-3 ml-2">
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedId(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                    >
                      ✏️ {translate('formBuilder.edit')}
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => moveFieldUp(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                      title={translate('formBuilder.moveUp')}
                    >
                      ▲
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => moveFieldDown(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                      title={translate('formBuilder.moveDown')}
                    >
                      ▼
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => deleteField(f.id)}
                      className="border border-white/35 bg-white/85 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/95 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                    >
                      🗑 {translate('formBuilder.delete')}
                    </motion.button>
                  </div>

                  {topLevelIndex === fields.filter(field => !branchTargets.has(field.id)).length - 1 && (
                    <CanvasInsertBar id={`canvas-insert:${fields.length}`} />
                  )}
                </div>
              ) : isWorkflowSection(f) ? (
                <div key={f.id}>
                  {/* insert bar ABOVE section */}
                  <CanvasInsertBar id={`canvas-insert:${idx}`} />

                  {/* section block with its own blue drop area for children */}
                  <SectionCanvas
                    f={f}
                    selectedId={selectedId}
                    setSelectedId={setSelectedId}
                    branchIndex={branchIndex}
                    duplicateField={duplicateField}
                    updateField={updateField}
                    updateFieldConfig={updateFieldConfig}
                  />

                  {/* quick actions for section itself */}
                  <div className="flex gap-2 -mt-1 mb-3 ml-2">
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedId(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                    >
                      ✏️ {translate('formBuilder.edit')}
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => moveFieldUp(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                      title={translate('formBuilder.moveUp')}
                    >
                      ▲
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => moveFieldDown(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                      title={translate('formBuilder.moveDown')}
                    >
                      ▼
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => deleteField(f.id)}
                      className="border border-white/35 bg-white/85 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/95 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                    >
                      🗑 {translate('formBuilder.delete')}
                    </motion.button>
                  </div>

                  {/* insert bar BELOW last item */}
                  {topLevelIndex === fields.filter(field => !branchTargets.has(field.id)).length - 1 && (
                    <CanvasInsertBar id={`canvas-insert:${fields.length}`} />
                  )}
                </div>
              ) : (
                <div key={f.id}>
                  {/* insert bar ABOVE field */}
                  <CanvasInsertBar id={`canvas-insert:${idx}`} />

                  {/* regular field */}
                  <SortableItem
                    id={f.id}
                    field={f}
                    selected={selectedId === f.id}
                    onSelect={setSelectedId}
                    visualDepth={0}
                    isBranched={false}
                    onDuplicate={duplicateField}
                  />

                  {/* quick actions for field */}
                  <div className="flex gap-2 -mt-1 mb-3 ml-2">
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedId(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                    >
                      ✏️ {translate('formBuilder.edit')}
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => moveFieldUp(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                      title={translate('formBuilder.moveUp')}
                    >
                      ▲
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => moveFieldDown(f.id)}
                      className="border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                      title={translate('formBuilder.moveDown')}
                    >
                      ▼
                    </motion.button>
                    <motion.button
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => deleteField(f.id)}
                      className="border border-white/35 bg-white/85 dark:bg-white/10 rounded-md px-3 py-1 text-xs text-gray-900 dark:text-white shadow-sm hover:bg-white/95 dark:hover:bg-white/20 transition-colors backdrop-blur-md"
                    >
                      🗑 {translate('formBuilder.delete')}
                    </motion.button>
                  </div>

                  {/* Render branch children underneath parent */}
                  {renderBranchChildren(f)}

                  {/* insert bar BELOW last item */}
                  {topLevelIndex === fields.filter(field => !branchTargets.has(field.id)).length - 1 && (
                    <CanvasInsertBar id={`canvas-insert:${fields.length}`} />
                  )}
                </div>
              );
            });
        })()
      )}
    </AnimatePresence>
  </SortableContext>
</div>
    </>
  ) : activeBuilderTab === "schedules" ? (
    /* Schedules View */
    <div className="py-4">
      <ScheduleManager formId={formId} companyId={companyId} />
    </div>
  ) : (
    /* Approvals View */
    <div className="py-4">
      <ApprovalWorkflowManager formId={formId} companyId={companyId} />
    </div>
  )}


</motion.main>

{/* Handle: Canvas | Props */}
<ResizeHandle onMouseDown={(e) => startResize("canvas", e)} />

{/* -------------------- Field Properties Panel -------------------- */}
<motion.section
  initial={false}
  animate={{ opacity: collapsed.props ? 0 : 1 }}
  transition={{ duration: 0.18 }}
  style={propsStyle}
  className="border-l border-white/10 bg-white/5 backdrop-blur-xl p-4 overflow-auto transition-colors text-white"
>
  <div className="flex items-center justify-between">
    <h3 className="text-white font-semibold mt-0">{translate('formBuilder.fieldProperties')}</h3>
    <button
      onClick={() => toggleCollapse("props")}
      className="text-xs px-2 py-1 border border-white/20 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
      title={translate('formBuilder.collapseProperties')}
    >
      ⟨
    </button>
  </div>

  {!selected ? (
    <p className="text-sm text-white/70">
      {translate('formBuilder.selectFieldToEdit')}
    </p>
  ) : (
    <div>
      {/* ---------------- Label ---------------- */}
      <label className="block text-xs text-white mb-1">{translate('formBuilder.label')}</label>
      <input
        value={selected.label}
        onChange={(e) => {
          const newLabel = e.target.value;
          updateField(selected.id, { label: newLabel });
        }}
        className="w-full bg-slate-900/70 border border-white/30 rounded-md px-3 py-2 mb-3 text-white placeholder-white/60 shadow-sm"
      />

      {/* ---------------- Common Toggles ---------------- */}
      {selected.type !== "instruction" && (
        <div className="flex flex-col gap-2 mb-3">
          {/* Required */}
          <label className="inline-flex items-center gap-2 text-xs text-white">
            <input
              type="checkbox"
              checked={selected.required || false}
              onChange={(e) =>
                updateField(selected.id, { required: e.target.checked })
              }
            />
            {translate('formBuilder.required')}
          </label>

          {/* ✅ Searchable */}
          <label className="inline-flex items-center gap-2 text-xs text-white">
            <input
              type="checkbox"
              checked={!!selected.config?.searchable}
              onChange={(e) =>
                updateFieldConfig(selected.id, { searchable: e.target.checked })
              }
            />
            {translate('formBuilder.searchableDescription')}
          </label>
        </div>
      )}

      {/* 🧩 Section Settings */}
      {isWorkflowSection(selected) && (
        <div className="flex flex-col gap-2 mb-3">
          <label className="inline-flex items-center gap-2 text-xs text-white">
            <input
              type="checkbox"
              checked={!!selected.config?.repeatable}
              onChange={(e) =>
                updateFieldConfig(selected.id, { repeatable: e.target.checked })
              }
            />
            {translate('formBuilder.repeatableSection')}
          </label>
        </div>
      )}

      {selected.type === SPLIT_WORKFLOW_TYPE && (
        <div className="mb-4 rounded-lg border border-amber-300/30 bg-amber-500/10 p-3 space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-100/90">
              Split Workflow
            </div>
            <p className="mt-1 text-xs text-white/80">
              Assign this section to specific users or groups and decide whether later assignees can view previous section responses.
            </p>
          </div>

          <AccessAssignment
            assignedUsers={selected.config?.assignedUsers || []}
            assignedGroups={selected.config?.assignedGroups || []}
            allUsers={selected.config?.allUsers !== false}
            allGroups={!!selected.config?.allGroups}
            onChange={({ assignedUsers: newUsers, assignedGroups: newGroups, allUsers: newAllUsers, allGroups: newAllGroups }) => {
              updateFieldConfig(selected.id, {
                assignedUsers: newUsers,
                assignedGroups: newGroups,
                allUsers: newAllUsers,
                allGroups: newAllGroups,
              });
            }}
          />

          <div>
            <label className="block text-[11px] text-white/80 mb-1">Previous Section Visibility</label>
            <select
              value={selected.config?.previousSectionVisibility || "visible"}
              onChange={(e) => updateFieldConfig(selected.id, { previousSectionVisibility: e.target.value })}
              className="w-full bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white shadow-sm"
            >
              <option value="visible">Assigned users can view previous section data</option>
              <option value="hidden">Assigned users cannot view previous section data</option>
            </select>
          </div>
        </div>
      )}

      {/* Type-specific */}
      {selected.type === "document_number" && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="col-span-2">
            <label className="block text-[11px] text-white">{translate('formBuilder.prefix')}</label>
            <input
              value={selected.config.prefix}
              onChange={(e) => updateFieldConfig(selected.id, { prefix: e.target.value })}
              className="w-full bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white placeholder-white/60 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] text-white">{translate('formBuilder.pad')}</label>
            <input
              type="number"
              min={1}
              value={selected.config.pad}
              onChange={(e) => updateFieldConfig(selected.id, { pad: Number(e.target.value) || 1 })}
              className="w-full bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white placeholder-white/60 shadow-sm"
            />
          </div>
        </div>
      )}

      {selected.type === "date_time" && (
        <div className="mb-3">
          <label className="block text-[11px] text-white">{translate('formBuilder.mode')}</label>
          <select
            value={selected.config.mode}
            onChange={(e) => updateFieldConfig(selected.id, { mode: e.target.value })}
            className="w-full bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white shadow-sm"
          >
            <option value="datetime">{translate('formBuilder.dateAndTime')}</option>
            <option value="date">{translate('formBuilder.dateOnly')}</option>
            <option value="time">{translate('formBuilder.timeOnly')}</option>
          </select>
        </div>
      )}

      {selected.type === "number" && (
        <div className="mb-3">
          <label className="block text-[11px] text-white">{translate('formBuilder.unitFormat')}</label>
          <select
            value={selected.config?.unit || "none"}
            onChange={(e) => updateFieldConfig(selected.id, { unit: e.target.value })}
            className="w-full bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white shadow-sm"
          >
            <option value="none">{translate('formBuilder.noUnit')}</option>
            <optgroup label={translate('formBuilder.distance')}>
              <option value="inches">Inches (in)</option>
              <option value="feet">Feet (ft)</option>
              <option value="miles">Miles (mi)</option>
              <option value="millimeters">Millimeters (mm)</option>
              <option value="centimeters">Centimeters (cm)</option>
              <option value="meters">Meters (m)</option>
              <option value="kilometers">Kilometers (km)</option>
            </optgroup>
            <optgroup label={translate('formBuilder.volume')}>
              <option value="liters">Liters (L)</option>
              <option value="gallons">Gallons (gal)</option>
            </optgroup>
            <optgroup label={translate('formBuilder.time')}>
              <option value="hours">Hours (hr)</option>
              <option value="minutes">Minutes (min)</option>
              <option value="seconds">Seconds (sec)</option>
            </optgroup>
            <optgroup label={translate('formBuilder.temperature')}>
              <option value="celsius">Celsius (°C)</option>
              <option value="fahrenheit">Fahrenheit (°F)</option>
            </optgroup>
            <optgroup label={translate('formBuilder.weight')}>
              <option value="ounces">Ounces (oz)</option>
              <option value="pounds">Pounds (lb)</option>
              <option value="tonnes">Tonnes (t)</option>
              <option value="grams">Grams (g)</option>
              <option value="kilograms">Kilograms (kg)</option>
            </optgroup>
          </select>
        </div>
      )}

      {selected.type === "asset" && (
        <div className="mb-3 space-y-3">
          <label className="inline-flex items-center gap-2 text-xs text-white">
            <input
              type="checkbox"
              checked={!!selected.config?.multiSelect}
              onChange={(e) => updateFieldConfig(selected.id, { multiSelect: e.target.checked })}
            />
            {translate('formBuilder.allowMultiAsset')}
          </label>

          <label className="inline-flex items-center gap-2 text-xs text-white">
            <input
              type="checkbox"
              checked={!!selected.config?.filterByType}
              onChange={(e) => updateFieldConfig(selected.id, { filterByType: e.target.checked })}
            />
            {translate('formBuilder.filterByAssetType')}
          </label>

          {selected.config?.filterByType && (
            <div>
              <label className="block text-[11px] text-white/80 mb-1">{translate('formBuilder.assetType')}</label>
              <input
                type="text"
                value={selected.config?.assetType || ''}
                onChange={(e) => updateFieldConfig(selected.id, { assetType: e.target.value })}
                placeholder={translate('formBuilder.assetTypePlaceholder')}
                className="w-full bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white placeholder-white/60 shadow-sm"
              />
            </div>
          )}

          {/* Asset Selection List */}
          <div className="border-t dark:border-gray-700 pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] text-gray-500 dark:text-gray-400">{translate('formBuilder.selectAssets')}</label>
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    const filteredAssets = selected.config?.filterByType && selected.config?.assetType
                      ? assets.filter(a => a.type === selected.config.assetType)
                      : assets;
                    const allIds = filteredAssets.map(a => a.id);
                    updateFieldConfig(selected.id, { selectedAssets: allIds });
                  }}
                  className="text-[10px] px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40"
                >
                  {translate('formBuilder.selectAll')}
                </button>
                <button
                  onClick={() => updateFieldConfig(selected.id, { selectedAssets: [] })}
                  className="text-[10px] px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {translate('formBuilder.clear')}
                </button>
              </div>
            </div>
            
            <div className="max-h-80 overflow-y-auto border dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900/50">
              {!companyId ? (
                <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
                  {translate('formBuilder.loadingAssets')}
                </div>
              ) : assets.length === 0 ? (
                <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
                  {translate('formBuilder.noAssetsAvailable')}
                </div>
              ) : (
                <>
                  {/* Unfiled Assets */}
                  {(() => {
                    const unfiledAssets = assets.filter(a => {
                      const matchesFolder = !a.folderId;
                      const matchesType = !selected.config?.filterByType || !selected.config?.assetType || a.type === selected.config.assetType;
                      return matchesFolder && matchesType;
                    });
                    
                    if (unfiledAssets.length === 0) return null;
                    
                    return (
                      <div className="border-b dark:border-gray-700">
                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-400">
                          📦 {translate('formBuilder.unfiled')}
                        </div>
                        <div className="p-2 space-y-1">
                          {unfiledAssets.map(asset => {
                            const isSelected = (selected.config?.selectedAssets || []).includes(asset.id);
                            return (
                              <label
                                key={asset.id}
                                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const current = selected.config?.selectedAssets || [];
                                    const updated = e.target.checked
                                      ? [...current, asset.id]
                                      : current.filter(id => id !== asset.id);
                                    updateFieldConfig(selected.id, { selectedAssets: updated });
                                  }}
                                  className="rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                                    {asset.title || translate('formBuilder.untitled')}
                                  </div>
                                  {asset.type && (
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                      {asset.type}{asset.role && ` • ${asset.role}`}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Assets by Folder */}
                  {assetFolders.map(folder => {
                    const folderAssets = assets.filter(a => {
                      const matchesFolder = a.folderId === folder.id;
                      const matchesType = !selected.config?.filterByType || !selected.config?.assetType || a.type === selected.config.assetType;
                      return matchesFolder && matchesType;
                    });

                    if (folderAssets.length === 0) return null;

                    return (
                      <div key={folder.id} className="border-b dark:border-gray-700 last:border-b-0">
                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 text-xs font-semibold text-gray-600 dark:text-gray-400">
                          📁 {folder.name}
                        </div>
                        <div className="p-2 space-y-1">
                          {folderAssets.map(asset => {
                            const isSelected = (selected.config?.selectedAssets || []).includes(asset.id);
                            return (
                              <label
                                key={asset.id}
                                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const current = selected.config?.selectedAssets || [];
                                    const updated = e.target.checked
                                      ? [...current, asset.id]
                                      : current.filter(id => id !== asset.id);
                                    updateFieldConfig(selected.id, { selectedAssets: updated });
                                  }}
                                  className="rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                                    {asset.title || translate('formBuilder.untitled')}
                                  </div>
                                  {asset.type && (
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                                      {asset.type}{asset.role && ` • ${asset.role}`}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Show message if assets exist but none match filters */}
                  {(() => {
                    const filteredCount = assets.filter(a => {
                      const matchesType = !selected.config?.filterByType || !selected.config?.assetType || a.type === selected.config.assetType;
                      return matchesType;
                    }).length;
                    
                    if (filteredCount === 0 && assets.length > 0) {
                      return (
                        <div className="p-4 text-center text-xs text-white/70">
                          {translate('formBuilder.noAssetsMatchFilter')} "{selected.config?.assetType}"
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </div>
            
            {/* Selection summary */}
            {(selected.config?.selectedAssets || []).length > 0 && (
              <div className="mt-2 text-[10px] text-white/70">
                {selected.config.selectedAssets.length} {translate('formBuilder.assetsSelected')}
              </div>
            )}
          </div>
        </div>
      )}

      {selected.type === "instruction" && (
        <div className="mb-3">
          <label className="block text-[11px] text-white/80 mb-1">{translate('formBuilder.instructionText')}</label>
          <textarea
            rows={4}
            value={selected.config.text}
            onChange={(e) => updateFieldConfig(selected.id, { text: e.target.value })}
            className="w-full bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white placeholder-white/60 shadow-sm"
          />
        </div>
      )}

      {selected.type === "multiple_choice" && (
        <div className="mb-3">
          <label className="block text-[11px] text-white/80">{translate('formBuilder.optionsScoresColours')}</label>
          <div className="mt-1 space-y-2">
            {(selected.config.options || []).map((opt, i) => (
              <div key={opt.id} className="grid grid-cols-[1fr_76px_58px_28px] gap-2 items-center">
                <input
                  value={opt.label}
                  onChange={(e) => {
                    const options = [...selected.config.options];
                    options[i] = { ...options[i], label: e.target.value };
                    updateFieldConfig(selected.id, { options });
                  }}
                  placeholder={translate('formBuilder.label')}
                  className="bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white placeholder-white/60 shadow-sm"
                />
                <input
                  type="number"
                  value={opt.score}
                  onChange={(e) => {
                    const options = [...selected.config.options];
                    options[i] = { ...options[i], score: Number(e.target.value) || 0 };
                    updateFieldConfig(selected.id, { options });
                  }}
                  placeholder={translate('formBuilder.score')}
                  className="bg-slate-900/70 border border-white/30 rounded-md px-2 py-2 text-sm text-white placeholder-white/60 shadow-sm"
                />
                <input
                  type="color"
                  value={opt.color || "#e5e7eb"}
                  onChange={(e) => {
                    const options = [...selected.config.options];
                    options[i] = { ...options[i], color: e.target.value };
                    updateFieldConfig(selected.id, { options });
                  }}
                  title={translate('formBuilder.optionColour')}
                  className="h-[36px] w-full p-0 border border-white/30 rounded-md bg-slate-900/70 transition-colors"
                />
                <button
                  onClick={() => {
                    const options = selected.config.options.filter((o) => o.id !== opt.id);
                    updateFieldConfig(selected.id, { options });
                  }}
                  className="border border-white/30 bg-slate-900/70 rounded-md text-xs text-white shadow-sm hover:bg-white/10 transition-colors"
                  title={translate('formBuilder.removeOption')}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() =>
              updateFieldConfig(selected.id, {
                options: [
                  ...(selected.config.options || []),
                  { id: nanoid(), label: translate('formBuilder.newOption'), score: 0, color: "#e5e7eb" },
                ],
              })
            }
            className="mt-2 border border-white/30 bg-slate-900/70 rounded-md px-3 py-2 text-xs text-white shadow-sm hover:bg-white/10 transition-colors"
          >
            ➕ {translate('formBuilder.addOption')}
          </button>
          <div className="mt-3 flex gap-4">
            <label className="inline-flex items-center gap-2 text-xs text-white">
              <input
                type="checkbox"
                checked={!!selected.config.multi}
                onChange={(e) => updateFieldConfig(selected.id, { multi: e.target.checked })}
              />
              {translate('formBuilder.allowMultipleSelection')}
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-white">
              <input
                type="checkbox"
                checked={!!selected.config.allowOther}
                onChange={(e) => updateFieldConfig(selected.id, { allowOther: e.target.checked })}
              />
              {translate('formBuilder.allowOther')}
            </label>
          </div>
        </div>
      )}

      {selected.type === "yesno" && (
        <div className="mb-3">
          <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-2">{translate('formBuilder.yesNoColours')}</label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] text-gray-600 dark:text-gray-300 mb-1">{translate('formBuilder.yesColour')}</p>
              <input
                type="color"
                value={selected.config?.yesColor || "#22c55e"}
                onChange={(e) => updateFieldConfig(selected.id, { yesColor: e.target.value })}
                className="w-full h-[42px] p-0 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 transition-colors"
                title={translate('formBuilder.colourForYes')}
              />
            </div>
            <div>
              <p className="text-[11px] text-gray-600 dark:text-gray-300 mb-1">{translate('formBuilder.noColour')}</p>
              <input
                type="color"
                value={selected.config?.noColor || "#ef4444"}
                onChange={(e) => updateFieldConfig(selected.id, { noColor: e.target.value })}
                className="w-full h-[42px] p-0 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 transition-colors"
                title={translate('formBuilder.colourForNo')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Attachments */}
      <div className="mt-4 pt-3 border-t border-white/20">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold m-0">📎 {translate('formBuilder.attachments')}</h4>
          {uploadingField === selected.id && (
            <span className="text-xs text-white/70 animate-pulse">{translate('formBuilder.uploading')}</span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap gap-2">
          {(selected.attachments || []).map((att) => (
            <div key={att.url} className="flex items-center gap-2 bg-slate-900/70 border border-white/30 rounded-md px-2 py-1 transition-colors shadow-sm">
              {isImage(att.type) ? (
                <img src={att.url} alt={att.name} className="w-8 h-8 object-cover rounded-sm" />
              ) : (
                <span className="text-sm">{iconFor(att.type)}</span>
              )}
              <span className="text-xs text-white/80 max-w-[140px] truncate" title={att.name}>
                {att.name}
              </span>
              <button
                className="text-rose-300 text-xs hover:text-rose-200"
                title={translate('formBuilder.remove')}
                onClick={() => onRemoveAttachment(selected.id, att.url)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3">
          <label className="inline-flex items-center gap-2">
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                onAddAttachment(selected.id, file);
              }}
            />
            <span className="border border-white/30 bg-slate-900/70 rounded-md px-3 py-2 text-xs text-white shadow-sm hover:bg-white/10 cursor-pointer select-none transition-colors">
              📎 {translate('formBuilder.addFile')}
            </span>
          </label>
        </div>
      </div>

      {/* Conditional Branching */}
      <BranchEditor
        sourceField={selected}
        allFields={fields}
        ensureBranchEntry={ensureBranchEntry}
        toggleNotify={toggleBranchNotify}
        removeTarget={removeBranchTarget}
      />
    </div>
  )}
</motion.section>

{/* Handle: Props | Preview */}
<ResizeHandle onMouseDown={(e) => startResize("props", e)} />

{/* -------------------- Live Preview Panel -------------------- */}
  <motion.aside
  initial={false}
  animate={{ opacity: collapsed.preview ? 0 : 1 }}
  transition={{ duration: 0.18 }}
  style={previewStyle}
  className="border-l border-white/10 p-4 overflow-hidden flex flex-col items-center bg-white/5 backdrop-blur-xl transition-colors text-white"
>
  {/* Header controls */}
    <div className="flex items-center justify-between w-full">
      <h3 className="text-white font-semibold m-0">{translate('formBuilder.livePreview')}</h3>
      <div className="flex items-center gap-2">
        <button
          onClick={() => toggleCollapse("preview")}
          className="text-xs px-2 py-1 border border-white/20 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
          title={translate('formBuilder.collapsePreview')}
        >
          ⟨
        </button>
      </div>
    </div>

  {/* Device frame */}
  <div
    ref={previewHostRef}
    className="mt-4 relative flex-1 w-full min-h-[420px] overflow-hidden"
  >
  <motion.div
    className="relative mx-auto flex items-center justify-center"
    transition={{ duration: 0.3 }}
    animate={{ opacity: 1, scale: 1 }}
    initial={{ opacity: 0.98, scale: 0.995 }}
    style={{
      width: previewDeviceConfig.frameWidth * previewScale,
      height: previewDeviceConfig.frameHeight * previewScale,
    }}
  >
    <div
      className="absolute top-0 left-1/2 flex items-center justify-center"
      style={{
        width: previewDeviceConfig.frameWidth,
        height: previewDeviceConfig.frameHeight,
        transform: `translateX(-50%) scale(${previewScale})`,
        transformOrigin: "top center",
      }}
    >
      <div
        className="absolute inset-0 rounded-[28px] shadow-[0_8px_20px_rgba(0,0,0,0.4)]"
        style={{
          background:
            "linear-gradient(145deg, #1e1e1e, #0d0d0d 70%, #262626)",
        }}
      />
      <div
        className="absolute inset-0 rounded-[28px] pointer-events-none opacity-40"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.3), transparent 60%)",
        }}
      />
      <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-3 h-3 rounded-full bg-gray-700 shadow-inner" />

      {/* Screen content */}
      <motion.div
        ref={previewScrollRef}
        className="relative bg-white dark:bg-gray-800 overflow-auto rounded-[20px] shadow-inner transition-colors text-slate-900 dark:text-gray-100"
        initial={{ opacity: 0.98 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
        onScroll={(event) => setPreviewScrollTop(event.currentTarget.scrollTop)}
        style={{
          width: previewDeviceConfig.screenWidth,
          height: previewDeviceConfig.screenHeight,
        }}
      >
      {/* Top logo + title */}
      <img
        src={logo}
        alt="logo"
        className="block mx-auto mt-4"
        style={{ width: device === "mobile" ? 80 : 120 }}
      />
      <div className="flex items-center justify-center gap-3">
        <h4 className="text-center text-rose-700 mt-2 mb-3">
          {title || translate('formBuilder.untitledForm')}
        </h4>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDevice((d) => (d === "mobile" ? "tablet" : "mobile"))}
            className="text-xs px-2 py-1 rounded border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-colors"
            title={translate('formBuilder.toggleDeviceView')}
          >
            {device === "mobile" ? `📱 ${translate('formBuilder.mobile')}` : `💻 ${translate('formBuilder.tablet')}`}
          </button>
          <button
            onClick={() => setCleanPreviewMode((s) => !s)}
            className="text-xs px-2 py-1 rounded border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-colors"
            title={cleanPreviewMode ? "Disable clean preview mode" : "Enable clean preview mode"}
          >
            {cleanPreviewMode ? "✨ Clean" : "🛠 Edit"}
          </button>
          <button
            onClick={resetPreviewLayout}
            className="text-xs px-2 py-1 rounded border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Reset preview layout"
          >
            ↺ Reset
          </button>
          {device === "tablet" && (
            <button
              onClick={() => setShowPropsInTablet((s) => !s)}
              className="text-xs px-2 py-1 rounded border border-white/20 bg-white/10 hover:bg-white/20 text-white transition-colors"
              title={showPropsInTablet ? translate('formBuilder.hideProps') : translate('formBuilder.showProps')}
            >
              ⚙️ {showPropsInTablet ? translate('formBuilder.hideProps') : translate('formBuilder.showProps')}
            </button>
          )}
        </div>
      </div>

      <div className={`${device === "mobile" ? "px-3 pb-8" : "px-4 pb-10"}`}>
        <AnimatePresence initial={false}>
          {/* 🧠 Recursive field renderer with branch nesting */}
          {(() => {
            function renderFieldWithBranches(field, depth = 0, localFields = fields, previewValuesParam = previewValues) {

  // SECTION SUPPORT 💙
  if (isWorkflowSection(field)) {
  if (field.type === SPLIT_WORKFLOW_TYPE) {
  return (
    <motion.div
      key={field.id}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      className="border-2 rounded-2xl p-4 mb-4 shadow-sm border-amber-300 bg-amber-50/40 dark:bg-amber-900/20"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-300">
            🔀 {field.label || translate('formBuilder.fieldSplitWorkflow')}
          </h4>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
            Subsequent fields continue in order and are assigned within this workflow section.
          </p>
        </div>
        <div className="text-xs text-amber-700 dark:text-amber-300 whitespace-nowrap">
          {field.config?.previousSectionVisibility === "hidden" ? "Previous hidden" : "Previous visible"}
        </div>
      </div>
    </motion.div>
  );
}

  const repeatable = !!field.config?.repeatable;
  const instances = field.instances || [field];
  const sectionIcon = field.type === SPLIT_WORKFLOW_TYPE ? "🔀" : "📁";
  const sectionAccent = field.type === SPLIT_WORKFLOW_TYPE
    ? "border-amber-300 bg-amber-50/40 dark:bg-amber-900/20"
    : "border-blue-300 bg-blue-50/40 dark:bg-blue-900/30";
  const titleAccent = field.type === SPLIT_WORKFLOW_TYPE
    ? "text-amber-700 dark:text-amber-300"
    : "text-blue-700 dark:text-blue-300";

  return (
    <motion.div
      key={field.id}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      className={`border-2 rounded-2xl p-4 mb-4 shadow-sm ${sectionAccent}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-semibold flex items-center gap-2 ${titleAccent}`}>
          {sectionIcon} {field.label || translate('formBuilder.untitledSection')}
        </h4>

        {repeatable && (
          <button
            type="button"
            onClick={() => {
              const copy = JSON.parse(JSON.stringify(field));
              copy.id = nanoid();
              copy.children = (field.children || []).map((c) => ({
                ...JSON.parse(JSON.stringify(c)),
                id: nanoid(),
              }));
              setFields((prev) =>
                prev.map((f) =>
                  f.id === field.id
                    ? { ...f, instances: [...(f.instances || []), copy] }
                    : f
                )
              );
            }}
            className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded"
          >
            ➕ {translate('formBuilder.addAnother')}
          </button>
        )}
      </div>

      {instances.map((inst, i) => (
        <div
          key={inst.id}
          className="mb-3 border border-white/20 rounded-lg p-3 bg-white/50 dark:bg-slate-950/20"
        >
          {repeatable && instances.length > 1 && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-white/70">{translate('formBuilder.instance')} #{i + 1}</span>
              <button
                type="button"
                className="text-xs text-rose-500 hover:text-rose-700"
                onClick={() =>
                  setFields((prev) =>
                    prev.map((f) =>
                      f.id === field.id
                        ? {
                            ...f,
                            instances: instances.filter((x) => x.id !== inst.id),
                          }
                        : f
                    )
                  )
                }
              >
                ✕ {translate('formBuilder.removeInstance')}
              </button>
            </div>
          )}

          {/* ✅ Render only non-branch children here; branch targets appear under their trigger */}
          {(inst.children || [])
            .filter((child) => !allBranchTargets.has(child.id))
            .map((child) =>
              isVisible(child.id)
                ? renderFieldWithBranches(child, depth + 1, inst.children, previewValuesParam)
                : null
            )}
        </div>
      ))}
    </motion.div>
  );
}

  // EXISTING LOGIC 🧠 (for normal fields)
  // ✅ Look up current field from main state to ensure latest config
  const currentField = findFieldById(field.id, fields) || field;
  const groups = getBranchGroups(currentField);
  const visibleChildren = [];

  // 🔍 Highlight logic (bi-directional)
const isLinked =
  selectedId &&
  fields.some((f) =>
    Object.values(f.branchMap || {}).some(
      (g) =>
        g.targets?.includes(field.id) ||
        (f.id === field.id && g.targets?.includes(selectedId))
    )
  );

// Tailwind class for highlight ring
const highlightClass =
  "ring-2 ring-rose-400 ring-offset-2 dark:ring-offset-gray-900 transition-all";


  for (const g of groups) {
  const cfg = currentField.branchMap?.[g.key];
  if (!cfg?.targets?.length) continue;

  // ✅ Resolve the current value safely (works inside nested sections too)
  const currentValue = previewValuesParam[field.id] ?? (field.value ?? "");

  if (g.test(currentValue, currentField)) {
    cfg.targets.forEach((tid) => {
  // Try to resolve child only within this section or local context
  const child =
    localFields.find((c) => c.id === tid) ||
    (field.children?.find((c) => c.id === tid)) ||
    findFieldById(tid, fields) ||
    null;

  if (child && isVisible(tid)) visibleChildren.push(child);
});

  }
}

  const indentClass =
    depth > 0
      ? "border-l-4 border-rose-300/70 pl-4 ml-2 bg-rose-50/30 rounded-xl"
      : "";

  return (
    <motion.div
  key={field.id}
  initial={{ opacity: 0, y: 4 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ opacity: 0, y: 4 }}
  onClick={() => {
    if (isCleanPreviewActive) {
      setSelectedId(field.id);
    }
  }}
  className={`border-2 rounded-2xl p-4 mb-4 shadow-sm transition-all duration-200 ${
    isLinkedToSelected(field)
      ? "border-rose-400 bg-rose-50/70 dark:bg-rose-900/20 ring-2 ring-rose-400/50"
      : "border-rose-300 bg-rose-50/40 dark:bg-rose-900/10"
  } ${indentClass} ${isCleanPreviewActive ? "cursor-pointer" : ""}`}
>

      {/* Field itself */}
      <FieldRenderer
        formId={formId}
        field={field}
        value={previewValuesParam[field.id]}
        setValue={(v) => setValueFor(field.id, v)}
        values={previewValuesParam}
        setValueFor={setValueFor}
        allFields={localFields}
        branchDepth={depth}
        openViewer={openViewer}
        previewMode={true}
        companyId={companyId}
        // prevent FieldRenderer from also rendering inline branch children
        inlineChildrenHandledExternally={true}
      />

        {/* Quick actions for this rendered field (edit / move / delete) */}
        {(!isCleanPreviewActive || selectedId === field.id) && (
        <div className={`mt-3 flex flex-wrap justify-center gap-2 ${isCleanPreviewActive ? "sticky bottom-2 z-10 rounded-xl bg-white/85 px-2 py-2 shadow-md backdrop-blur dark:bg-slate-900/85" : ""}`}>
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedId(field.id)}
            className={`border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md ${device === "mobile" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1 text-xs"}`}
          >
            {device === "mobile" ? translate('formBuilder.edit') : `✏️ ${translate('formBuilder.edit')}`}
          </motion.button>
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => moveFieldUp(field.id)}
            className={`border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md ${device === "mobile" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1 text-xs"}`}
            title={translate('formBuilder.moveUp')}
          >
            {device === "mobile" ? "Up" : "▲"}
          </motion.button>
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => moveFieldDown(field.id)}
            className={`border border-white/30 dark:border-white/25 bg-white/80 dark:bg-white/10 rounded-md text-gray-900 dark:text-white shadow-sm hover:bg-white/90 dark:hover:bg-white/20 transition-colors backdrop-blur-md ${device === "mobile" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1 text-xs"}`}
            title={translate('formBuilder.moveDown')}
          >
            {device === "mobile" ? "Down" : "▼"}
          </motion.button>
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => deleteField(field.id)}
            className={`border border-rose-200/80 dark:border-rose-300/70 bg-rose-600/20 dark:bg-rose-600/30 rounded-md text-white shadow-sm hover:bg-rose-600/30 dark:hover:bg-rose-600/40 transition-colors backdrop-blur-md ${device === "mobile" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1 text-xs"}`}
          >
            {device === "mobile" ? translate('formBuilder.delete') : `🗑 ${translate('formBuilder.delete')}`}
          </motion.button>
        </div>
        )}

        {isCleanPreviewActive && selectedId !== field.id && (
          <div className="mt-3 text-center text-[11px] text-slate-500 dark:text-slate-300">
            Tap field to edit
          </div>
        )}

      {/* Attachments */}
      {field.attachments?.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap justify-center">
          {field.attachments.map((att) => (
            <img
              key={att.url}
              src={att.url}
              alt={att.name}
              className="w-14 h-14 object-cover rounded border border-gray-300 shadow-sm"
            />
          ))}
        </div>
      )}

      {/* Branching children */}
      {visibleChildren.length > 0 && (
        <div className="mt-4 space-y-3">
          {visibleChildren.map((child) =>
  renderFieldWithBranches(child, depth + 1, localFields, previewValuesParam)
)
}
        </div>
      )}
    </motion.div>
  );
}



// ✅ Collect every nested field id (children, instances, branch targets)
function collectAllNestedIds(list) {
  const ids = new Set();

  function walk(items) {
    for (const f of items) {
      // children
      if (f.children?.length) {
        f.children.forEach((c) => ids.add(c.id));
        walk(f.children);
      }

      // repeatable instances
      if (f.instances?.length) {
        f.instances.forEach((inst) => {
          if (inst.children?.length) {
            inst.children.forEach((c) => ids.add(c.id));
            walk(inst.children);
          }
        });
      }

      // branch targets
      for (const branch of Object.values(f.branchMap || {})) {
        if (Array.isArray(branch.targets)) branch.targets.forEach((tid) => ids.add(tid));
      }
    }
  }

  walk(list);
  return ids;
}

// ✅ Gather all nested IDs
const nestedIds = collectAllNestedIds(fields);

// ✅ Also include all direct children of sections
const sectionChildIds = new Set(
  fields.flatMap((f) => (f.children ? f.children.map((c) => c.id) : []))
);

// ✅ Combine both sets
const allNestedIds = new Set([...nestedIds, ...sectionChildIds]);

// ✅ Top-level fields = visible ones not nested anywhere
const topLevelFields = fields.filter(
  (f) => isVisible(f.id) && !allNestedIds.has(f.id)
);



// ✅ Render only true top-levels
return (
  <AnimatePresence mode="popLayout">
    {topLevelFields.length === 0 ? (
      <motion.p
        key="empty-prev"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="text-gray-500 dark:text-gray-400 text-center"
      >
        {translate('formBuilder.addFieldsToPreview')}
      </motion.p>
    ) : (
      topLevelFields.map((f) => (
        <motion.div
          key={f.id}
          layout
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
        >
          {renderFieldWithBranches(f)}
        </motion.div>
      ))
    )}
  </AnimatePresence>
);


          })()}
        </AnimatePresence>
      </div>

      {/* Footer watermark */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] tracking-widest text-rose-700/70"
      >
        {translate('formBuilder.poweredBy')}
      </motion.div>
      </motion.div>
    </div>
  </motion.div>
  </div>
</motion.aside>

{/* Drag overlay (toolbox label) */}
{/* Drag overlay (toolbox label) */}
<DragOverlay>
  {activeToolLabel ? (
    <div className="px-3 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 shadow-sm text-sm text-gray-800 dark:text-gray-100 transition-colors">
      {activeToolLabel}
    </div>
  ) : null}
</DragOverlay>

</div> {/* ✅ closes main flex wrapper started near line 850 */}

</DndContext>
);
}



// -------------------- Branch Editor (right panel) --------------------
function BranchEditor({
  sourceField,
  allFields,
  ensureBranchEntry,
  toggleNotify,
  removeTarget,
}) {
  const { t: translate } = useTranslation();
  // ⚙️ Ensure branch entries exist once per field (safe even if null)
  useEffect(() => {
    if (!sourceField) return;
    const branchGroups = getBranchGroups(sourceField);
    branchGroups.forEach((g) => ensureBranchEntry(sourceField.id, g.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceField?.id]);

  if (!sourceField) return null;

  const branchGroups = getBranchGroups(sourceField);

  if (!branchGroups.length) {
    return (
      <div className="mt-4 p-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-100 italic">
        {translate('formBuilder.noBranchingSupport')}
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
      <h4 className="text-sm font-semibold text-white mb-2">
        ⚡ {translate('formBuilder.conditionalBranching')}
      </h4>
      <p className="text-xs text-white/80 mb-3">
        {translate('formBuilder.branchDescription')}
      </p>

      {branchGroups.map((g) => {
  const cfg = sourceField.branchMap?.[g.key] || { notify: false, targets: [] };

  // ✅ Resolve target fields even if they live inside nested sections
  const findField = (id, list = allFields) => {
    for (const f of list) {
      if (f.id === id) return f;
      if (f.children?.length) {
        const found = findField(id, f.children);
        if (found) return found;
      }
    }
    return null;
  };

  return (
    <div
      key={g.key}
      className="mb-4 p-3 rounded-lg border border-white/35 bg-slate-900/70 backdrop-blur-md shadow-sm"
    >
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-xs font-semibold text-white flex items-center gap-1">
          {translate('formBuilder.whenAnswerIs')}
          <span className="px-2 py-0.5 rounded-full bg-rose-500/40 text-white font-semibold">{g.label}</span>
        </h5>
        <label className="text-xs flex items-center gap-1 text-white cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.notify}
            onChange={(e) =>
              toggleNotify(sourceField.id, g.key, e.target.checked)
            }
          />
          {translate('formBuilder.notify')}
        </label>
      </div>

      <BranchDropArea id={`branch-drop:${sourceField.id}:${g.key}`}>
        {(cfg.targets || []).length > 0 ? (
          cfg.targets.map((tid) => {
            const targetField = findField(tid);
            if (!targetField) return null;

            return (
              <div
                key={tid}
                className="flex items-center justify-between border border-white/25 rounded-md px-2 py-1 bg-slate-900/70 shadow-sm"
              >
                <span className="text-xs text-white">
                  🧩 {targetField.label || targetField.type}
                </span>
                <button
                  onClick={() => removeTarget(sourceField.id, g.key, tid)}
                  className="text-xs text-white hover:text-white/80"
                  title={translate('formBuilder.removeLinkedField')}
                >
                  ✕
                </button>
              </div>
            );
          })
        ) : (
          <p className="text-xs text-white italic text-center py-2">
            {translate('formBuilder.dropFieldsHere')}
          </p>
        )}
      </BranchDropArea>
    </div>
  );
})}

    </div>
  );
}

// ✅ PreviewField component has been replaced with FieldRenderer
// The new FieldRenderer provides:
// - Automatic field type routing
// - Built-in branching logic  
// - Consistent mobile-like styling
// - Field parts (attachments, uploads, notes)
// See components/FieldRenderer.jsx and components/fields/ for details
